// Parent product component
/**
 * All %-tiers from tag suffix (e.g. `3-20%-5-30` or `3-20%-5-30%`).
 * @returns {Array<{ qty: number, pct: number }>}
 */
function parseDkPdpPercentTiersFromSuffix(suffix) {
  if (!suffix || typeof suffix !== 'string' || !suffix.includes('%')) return [];
  const parts = suffix.split('-');
  const tiers = [];
  for (let i = 0; i + 1 < parts.length; i += 2) {
    const qty = parseInt(parts[i], 10);
    const pctPart = parts[i + 1];
    if (!pctPart) break;
    const pct = parseInt(String(pctPart).replace(/%/g, ''), 10);
    if (Number.isNaN(qty) || Number.isNaN(pct)) break;
    tiers.push({ qty, pct });
  }
  return tiers;
}

function joinDkOxfordList(items) {
  if (!items.length) return '';
  if (items.length === 1) return String(items[0]);
  if (items.length === 2) return `${items[0]} or ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, or ${items[items.length - 1]}`;
}

function formatDkPdpPercentBuyLine(tiers) {
  const fragments = tiers.map((t) => `${t.qty} for ${t.pct}% off`);
  return `Buy ${joinDkOxfordList(fragments)}`;
}

function dkPdpStripHtml(html) {
  if (!html) return '';
  const d = document.createElement('div');
  d.innerHTML = html;
  return (d.textContent || d.innerText || '').replace(/\s+/g, ' ').trim();
}

function formatDkPdpPercentInfoTooltip(templateRaw, tiers) {
  const plain = dkPdpStripHtml(templateRaw || '');
  if (!plain) return '';
  const parts = plain.split('|||').map((p) => p.trim()).filter(Boolean);
  if (!parts.length) return '';
  const head = parts[0];
  const foot = parts.length > 1 ? parts.slice(1).join(' ').trim() : '';
  const tierList = joinDkOxfordList(tiers.map((t) => t.qty));
  let out = head.replaceAll('#tier_list', tierList).replaceAll('#quantity', tierList);
  if (foot) out = `${out} ${foot}`.replace(/\s+/g, ' ').trim();
  return out;
}

/**
 * Suffix after Discount Kit tag prefix: fixed `3-39` or percent tiers `3-20%-5-30%`.
 * For percent, `quantity` / `pct` are the first tier (CTA / data attrs); `tiers` lists all tiers.
 * @returns {{ isPercent: boolean, quantity: number, priceUsd: number, priceCad: number, pct?: number, tiers?: Array<{ qty: number, pct: number }> }} | null
 */
function parseDkPdpBundleDiscountKitSuffix(quantityPriceText) {
  if (!quantityPriceText || typeof quantityPriceText !== 'string') return null;
  if (quantityPriceText.includes('%')) {
    const tiers = parseDkPdpPercentTiersFromSuffix(quantityPriceText);
    if (!tiers.length) return null;
    const { qty: quantity, pct } = tiers[0];
    return { isPercent: true, quantity, pct, priceUsd: 0, priceCad: 0, tiers };
  }
  const parts = quantityPriceText.split('-');
  const quantity = +parts[0];
  const priceUsd = +parts[1] * 100;
  if (Number.isNaN(quantity) || Number.isNaN(priceUsd)) return null;
  const priceCad = +parts[1] * 100;
  return { isPercent: false, quantity, priceUsd, priceCad };
}

function updateDkBundleSavePercentPdpCopy(bundleSaveTextEl, quantityPriceText, parsed) {
  if (!bundleSaveTextEl || !parsed?.isPercent) return;
  const tiers = parsed.tiers?.length ? parsed.tiers : parseDkPdpPercentTiersFromSuffix(quantityPriceText);
  if (!tiers?.length) return;
  const span = bundleSaveTextEl.querySelector('.js-dk-bundle-save-percent-line');
  if (span) span.textContent = ` ${formatDkPdpPercentBuyLine(tiers)}`;
  const tpl = bundleSaveTextEl.dataset.dkInfoPercentTemplate || '';
  const infoEl = bundleSaveTextEl.querySelector('.info-trigger');
  if (infoEl && tpl) {
    const tip = formatDkPdpPercentInfoTooltip(tpl, tiers);
    if (tip) infoEl.setAttribute('tooltip', tip);
  }
}

if (!customElements.get('component-pdp-product')) {
  customElements.define('component-pdp-product', class PDPProduct extends HTMLElement {
    constructor() {
      super();

      // State
      this.state = {
        product: {},
        selectedColor: '',
        selectedSize: '',
        isProductLoaded: false,
        colorCategory: {}
      }

      // Props
      this.props = {
        productHandle: this.dataset.productHandle,
        pdpId: this.dataset.pdpId,
        emailKey: '',
        useTrueSize: this.dataset.useTrueSize === 'true'
      }

      // DOM
      this.dom = {
        template: this.querySelector('template')
      }
    }
    async connectedCallback() {
      this.state.product = await this.getProduct();
      this.state.selectedColor = this.state.product.color;
      this.dispatchEvent(new CustomEvent('render-product'));
      setTimeout(() => {
        this.addAdwordsGTMData();
        this.sendViewItemGTMData();
      }, 0);

      // Listen for state change from product subcomponents
      this.addEventListener('set-state', (event) => {
        let detail = {};
        if (event.detail && event.detail.selectedColor) {
          const currentSiblingColor = this.state.product.colors[this.state.selectedColor];
          this.state.selectedColor = event.detail.selectedColor;
          this.updatePDPSections(currentSiblingColor);
        }
        if (event.detail && event.detail.selectedSize) {
          this.state.selectedSize = event.detail.selectedSize;
        }
        if (event.detail && event.detail.colorCategory) {
          this.state.colorCategory = {
            ...this.state.colorCategory,
            ...event.detail.colorCategory
          };
        }
        if (event.detail && event.detail.componentsToRender) {
          detail.componentsToRender = event.detail.componentsToRender;
        }

        this.dispatchEvent(new CustomEvent('render', { detail: detail }));
      });

      // Init selected size from filter parameter or true size
      let searchParams = new URLSearchParams(window.location.search);
      if (searchParams.get('size')) {
        this.initSelectedSize(searchParams.get('size'));
      } else if (this.props.useTrueSize) {
        await this.initSizeFromTrueSize();
      }
    }
    async getProduct() {
      this.dispatchEvent(new CustomEvent('product-loading'));
      // Get product data
      let response = await fetch(`${window.Shopify.routes.root}products/${this.props.productHandle}?view=pdp-product`, {
        method: "get"
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      response = await response.json();
      return response.product;
    }
    updatePDPSections(currentSiblingColor) {
      const newSiblingColor = this.state.product.colors[this.state.selectedColor];
      if (currentSiblingColor.reviews_count !== newSiblingColor.reviews_count && currentSiblingColor.reviews_average !== newSiblingColor.reviews_average) {
        document.dispatchEvent(new CustomEvent('update-pdp-product-reviews', { detail: { handle: newSiblingColor.handle } }));
      }
      document.dispatchEvent(new CustomEvent('update-pdp-sections', { detail: { handle: newSiblingColor.handle, productId: newSiblingColor.id } }));
    }
    async initSizeFromTrueSize() {
      const isUnderwear = this.state.product.type === 'Panties';
      const trueSize = await getTrueSize();
      let size;
      if (trueSize) {
        size = isUnderwear ? trueSize.underwear_size : `${trueSize.bra_band}${trueSize.bra_cup}`;
      }
      if (size) {
        this.initSelectedSize(size);
      }
    }
    initSelectedSize(size) {
      try {
        let sizeToSelect;
        if (this.state.product.type !== 'Panties') {
          if (this.state.product.tags.includes('precise_sizing')) {
            let mappedSize = getMappedFormfitSize(size);
            if (mappedSize) size = mappedSize;
          } else if (this.state.product.type !== "Bra" || this.state.product.tags.includes('alpha')) {
            let mappedSize = getMappedAlphaSize(size);
            if (mappedSize) size = mappedSize
          }
        }
        if (size) {
          if (this.state.product.colors[this.state.selectedColor].availableSizes.includes(size)) {
            sizeToSelect = size;
          } else if (this.state.product.type !== 'Panties') {
            // See if range is available instead
            let bandSize = size.substring(0, 2);
            let cupSize = size.substring(2);
            if (cupSize.includes('½')) {
              cupSize = `${cupSize.replace('½', '')}-${cupSize}`;
            } else {
              cupSize = `${cupSize}-${cupSize}½`;
            }
            if (this.state.product.colors[this.state.selectedColor].availableSizes.includes(`${bandSize}${cupSize}`)) {
              sizeToSelect = `${bandSize}${cupSize}`;
            }
          }
          if (sizeToSelect) {
            const detail = {
              selectedSize: sizeToSelect,
              componentsToRender: 'PDPProductVariants, PDPProductInfo'
            }
            this.dispatchEvent(new CustomEvent('set-state', {
              bubbles: true,
              detail: detail
            }));
          }
        }
      } catch (e) {
        console.error(e);
      }
    }
    addAdwordsGTMData() {
      const gtmInfo = this.querySelector('.js-gtm-info');
      if (this.state.selectedSize) {
        gtmInfo.querySelector('#adwords-id').innerHTML = this.state.product.colors[this.state.selectedColor]?.variants[this.state.selectedSize]?.sku;
      }
      gtmInfo.querySelector('#adword-total').innerHTML = this.state.product.colors[this.state.selectedColor]?.price;
      gtmInfo.querySelector('#gtm-page-category').innerHTML = this.state.product.handle;
    }
    sendViewItemGTMData() {
      window.sendGTMData && window.sendGTMData({
        event: 'pdp_view_item',
        product_id: this.state.product.id,
        source: 'pdp'
      });
    }
  });
}

// Boilerplate class used for all product subcomponents
class PDPProductSubComponent extends HTMLElement {
  constructor() {
    super();
    // Props
    this.props = {
      hydration: this.dataset.hydration && this.dataset.hydration === 'true' ? true : false
    }
    // DOM Elements
    this.dom = {
      parentComponent: this.closest('component-pdp-product'),
      template: this.querySelector('.js-template'),
      skeletonTemplate: this.querySelector('.js-template-skeleton')
    }
    if (this.props.hydration) {
      const template = document.createElement('template');
      template.content.appendChild(this.dom.template.cloneNode(true).querySelector('.js-container'));
      this.template = template;
    } else {
      this.template = this.dom.template;
    }
    this.dom.parentComponent.addEventListener('render', (event) => {
      // Render all components by default
      let componentsToRender = 'all';
      if (event.detail && event.detail.componentsToRender) {
        componentsToRender = event.detail.componentsToRender;
      }
      // Support ability to re-render only certain components using the component name
      const name = this.tagName.toLowerCase().replace(/-/g, '').replace(/component/g, '');
      const components = componentsToRender.toLowerCase();
      if (components === 'all' || components.includes(name)) {
        if (this.querySelector('.js-container')) {
          this.querySelector('.js-container').remove();
        }
        this.render();
      }
    });
    // Event to render all components after product is fetched
    this.dom.parentComponent.addEventListener('render-product', () => {
      this.render();
    });
  }
  append(template) {
    if (this.querySelector('.js-template') && this.querySelector('.js-template').nodeName === 'DIV') {
      this.querySelector('.js-template').remove();
    }
    this.appendChild(template);
  }
  getProduct() {
    return this.dom.parentComponent.state.product;
  }
  getSiblingColor() {
    return this.dom.parentComponent.state.product.colors[this.dom.parentComponent.state.selectedColor];
  }
  getVariant() {
    return this.dom.parentComponent.state.product.colors[this.dom.parentComponent.state.selectedColor].variants[this.dom.parentComponent.state.selectedSize];
  }
  hasBandCup() {
    return this.dom.parentComponent.state.product.type === "Bra" && !this.dom.parentComponent.state.product.tags.includes('alpha');
  }
  getModelInfo(id, attribute) {
    let size;
    let title;
    if (this.getProduct().type === 'Bra') {
      if (window.theme.settings.multipleModels.models[id]) {
        size = window.theme.settings.multipleModels.models[id].braSize;
        title = window.theme.settings.multipleModels.models[id].braTitle;
      }
    } else if (this.getProduct().type === 'Panties') {
      if (window.theme.settings.multipleModels.models[id]) {
        size = window.theme.settings.multipleModels.models[id].underwearSize;
        title = window.theme.settings.multipleModels.models[id].underwearTitle;
      }
    }
    if (attribute === 'size') {
      return size;
    } else if (attribute === 'title') {
      return title;
    } else {
      return window.theme.settings.multipleModels.models[id];
    }
  }
}

if (!customElements.get('component-pdp-product-gallery')) {
  customElements.define('component-pdp-product-gallery', class PDPProductGallery extends PDPProductSubComponent {
    constructor() {
      super();
      this.state = {
        firstRender: true,
        mediaIndex: 0,
        models: {},
        activeModelId: ''
      }
      this.props.badgePrefix = this.dataset.badgePrefix;
      this.props['rebuyApiKey'] = this.dataset.rebuyApiKey;
      this.props['rebuyEventApiUrl'] = this.dataset.rebuyEventApiUrl;
      this.props['productHandle'] = this.dataset.productHandle;
      this.closest('component-pdp-product').addEventListener('update-images', (event) => {
        if (event && event.detail.images) {
          // Update state with new images
          if (this.querySelector('.js-container')) {
            this.querySelector('.js-container').remove();
          }
          this.render(event.detail.images);
          if (this.dataset.isLanding !== 'true') {
            window.scrollTo(0, 0);
          }
        }
      });
    }
    async render(modelMedia) {
      const template = this.template.content.cloneNode(true);
      // Reset media index to show first image on color change
      this.state.mediaIndex = 0;
      if (!modelMedia) {
        this.state.activeModelId = '';
      }
      // Default image size matches size in 'json-color' snippet
      this.defaultImgSize = '300';
      if (this.getSiblingColor()) {
        let media = modelMedia ? modelMedia.slice() : this.getSiblingColor().media.slice();
        // Move main media to end of grid
        if (media.length > 2) {
          if (!media[0].alt.includes('model') || media[0].src.includes('PDP_FLAT')) {
            media.push(media.shift());
          }
        }
        this.state.media = media;
        if (this.props.hydration && !this.state.firstRender) {
          if (window.innerWidth < 768) {
            this.renderMobileGallery(template);
          } else {
            this.renderDesktopGallery(template);
          }
          this.append(template);
        }
        this.renderModelText(this);
        this.renderModels(this);
        this.renderControls(this);
        const badge = this.getBadge();
        if (badge) {
          this.renderBadge(this, badge);
        }
      }
      this.afterRender();
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(() => { this.notifyRebuyViewed(); });
      } else {
        setTimeout(() => { this.notifyRebuyViewed(); }, 300);
      }
    }
    renderDesktopGallery(template) {
      this.renderThumbnails(template);
      this.renderMedia(template);
    }
    renderThumbnails(template) {
      template.querySelectorAll('.js-thumbnail').forEach(item => item.remove());
      this.state.media.forEach((media, index) => {
        const thumbnail = document.createElement('img');
        thumbnail.dataset.srcset = getSrcset(media.src, this.defaultImgSize);
        thumbnail.dataset.sizes = 'auto';
        thumbnail.dataset.src = media.src;
        thumbnail.alt = media.alt;
        thumbnail.width = media.width;
        thumbnail.height = media.height;
        thumbnail.dataset.index = index;
        thumbnail.classList.add('lazyload', 'thumbnail', 'js-thumbnail');
        template.querySelector('.js-thumbnails').appendChild(thumbnail);
      });
    }
    renderMedia(template) {
      if (template.querySelector('.js-media')) template.querySelector('.js-media').remove();
      let mediaEl;
      const media = this.state.media[this.state.mediaIndex];
      if (!media) return;
      if (media.type === 'video' && media.videoSrc) {
        mediaEl = document.createElement('video');
        const sourceEl = document.createElement('source');
        mediaEl.controls = true;
        mediaEl.playsinline = true;
        sourceEl.src = media.videoSrc;
        sourceEl.type = `video/${media.videoFormat}`;
        mediaEl.appendChild(sourceEl);
      } else {
        mediaEl = document.createElement('img');
        mediaEl.dataset.srcset = getSrcset(media.src, this.defaultImgSize);
        mediaEl.dataset.sizes = 'auto';
        mediaEl.classList.add('lazyload');
        mediaEl.width = media.width;
        mediaEl.height = media.height;
        mediaEl.alt = media.alt;
      }
      mediaEl.classList.add('js-media', 'media');
      template.querySelector('.js-media-container').appendChild(mediaEl);
    }
    renderModelText(template) {
      const modelTemplate = template.querySelector('.js-template-model-text').content.cloneNode(true);
      let models = [];
      this.getSiblingColor().media.forEach((media) => {
        const alt = media.alt.trim().split('model')[1];
        if (alt && !models.includes(alt)) {
          models.push(alt);
        }
      });
      const size = this.updateModelSize(modelTemplate);
      if (size) {
        if (models.length > 1) {
          const modelTemplateBtn = modelTemplate.querySelector('.js-template-model-btn').content.cloneNode(true);
          modelTemplate.querySelector('.js-gallery-model-text').appendChild(modelTemplateBtn);
          modelTemplate.querySelector('.js-gallery-model-text').classList.add('gallery-model--with-btn');
        }
        template.querySelector('.js-media-container-footer').appendChild(modelTemplate.cloneNode(true));
        template.querySelector('.js-gallery-mobile .js-media-container-footer').appendChild(modelTemplate.cloneNode(true));
      }
    }
    updateModelSize(template) {
      const media = this.state.media[this.state.mediaIndex];
      const selectedMediaAlt = media.alt.trim().split('model')[1];
      if (selectedMediaAlt && !media.src.includes('HERO')) {
        const size = this.getModelInfo(selectedMediaAlt, 'size');
        if (size) {
          template.querySelector('.js-gallery-model-text .js-size').innerHTML = size;
          return size;
        }
      }
      return false;
    }
    renderModels(template) {
      const modelTemplate = template.querySelector('.js-template-models').content.cloneNode(true);
      // Reset state
      this.state.models = {};
      // Add model ids from images alt tags
      this.getSiblingColor().media.forEach((media) => {
        const alt = media.alt.trim().split('model')[1];
        if (alt) {
          if (!this.state.models[alt]) {
            this.state.models[alt] = [];
          }
          this.state.models[alt].push(media);
        }
      });
      if (Object.keys(this.state.models).length > 1) {
        for (const id in this.state.models) {
          const size = this.getModelInfo(id, 'size');
          if (size) {
            this.renderModelBtn(modelTemplate, size, id);
          }
        }
      }
      template.querySelector('.js-models-desktop').appendChild(modelTemplate.cloneNode(true));
      template.querySelector('.js-models-mobile').appendChild(modelTemplate.cloneNode(true));
    }
    renderModelBtn(template, size, id) {
      const modelTemplate = template.querySelector('.js-template-model').content.cloneNode(true);
      let imageSrc = this.state.models[id][0].src;
      if (imageSrc) {
        if (imageSrc.includes('width=300')) {
          // Optimize image size
          imageSrc = imageSrc.replace('width=300', 'width=54');
        }
        modelTemplate.querySelector('.js-model-img').src = imageSrc;
        modelTemplate.querySelector('.js-model-size').innerHTML = size;
        modelTemplate.querySelector('.js-model').dataset.id = id;
        modelTemplate.querySelector('.js-model').dataset.size = size;
        if (id === this.state.activeModelId) {
          modelTemplate.querySelector('.js-model-selected-text').classList.remove('hidden');
        }
        if (modelTemplate.querySelector('.js-model-text-subtext') && this.getModelInfo(id, 'title')) {
          modelTemplate.querySelector('.js-model-text-subtext').innerHTML = this.getModelInfo(id, 'title');
        }
        template.querySelector('.js-models').appendChild(modelTemplate);
      }
    }
    renderControls(template) {
      if (this.state.media.length > 1) {
        template.querySelectorAll('.js-gallery-prev').forEach(el => {
          el.setAttribute('disabled', 'disabled');
          el.classList.remove('hidden')
        });
        template.querySelectorAll('.js-gallery-next').forEach(el => el.classList.remove('hidden'));
      } else {
        template.querySelectorAll('.js-gallery-prev').forEach(el => el.classList.add('hidden'));
        template.querySelectorAll('.js-gallery-next').forEach(el => el.classList.add('hidden'));
      }
    }
    renderMobileGallery(template) {
      // Remove liquid rendered elements
      template.querySelectorAll('.js-gallery-mobile > *:not(template)').forEach(item => item.remove());
      template.querySelectorAll('.js-gallery-mobile-dots > *:not(template)').forEach(item => item.remove());
      // Ignore first image if first render (first image is rendered via liquid)
      if (this.state.firstRender) {
        this.state.media.shift();
      }
      const length = this.state.media.length;
      this.state.media.forEach((media, index) => {
        let mobileMedia;
        if (media.type === 'video') {
          mobileMedia = document.createElement('video');
          const sourceEl = document.createElement('source');
          mobileMedia.controls = true;
          mobileMedia.playsinline = true;
          mobileMedia.classList.add('js-mobile-media', 'mobile-media');
          sourceEl.src = media.videoSrc;
          sourceEl.type = `video/${media.videoFormat}`;
          mobileMedia.appendChild(sourceEl);
        } else {
          mobileMedia = document.createElement('img');
          mobileMedia.sizes = '100vw';
          mobileMedia.dataset.srcset = getSrcset(media.src, this.defaultImgSize);
          mobileMedia.alt = media.alt;
          mobileMedia.width = media.width;
          mobileMedia.height = media.height;
          mobileMedia.classList.add('mobile-media', 'js-mobile-media');
          if (index > 1) {
            mobileMedia.loading = 'lazy';
          }
        }
        mobileMedia.dataset.index = index;
        mobileMedia.classList.add(`js-mobile-media-${index}`);
        if (length < 2) mobileMedia.classList.add('only-image');
        template.querySelector('.js-gallery-mobile').appendChild(mobileMedia);
      });

      const arrows = template.querySelector('.js-template-gallery-arrows').content.cloneNode(true);
      template.querySelector('.js-gallery-mobile').appendChild(arrows);
      const footer = document.createElement('div');
      footer.classList.add('js-media-container-footer');
      template.querySelector('.js-gallery-mobile').appendChild(footer);
    }
    getBadge() {
      const tags = this.getSiblingColor().tags;
      let badge;
      for (let tag in tags) {
        if (typeof tags[tag] === 'string' && tags[tag].includes(this.props.badgePrefix)) {
          badge = tags[tag].replace(this.props.badgePrefix, '');
          break;
        }
      }
      return badge;
    }
    renderBadge(template, badge) {
      function updateBadge() {
        const badgeTemplate = template.querySelector('.js-template-gallery-badge').content.cloneNode(true);
        badgeTemplate.querySelector('.js-gallery-badge').innerHTML = badge;
        return badgeTemplate;
      }
      template.querySelector('.js-media-container-footer').append(updateBadge());
      template.querySelector('.js-gallery-mobile .js-media-container-footer').append(updateBadge());
    }
    handleMobileGallery() {
      this.querySelectorAll('.js-mobile-media').forEach(img => {
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
        }
      });

      const gallery = this.querySelector('.js-gallery-mobile');
      const prevBtn = gallery.querySelector('.js-gallery-prev');
      const nextBtn = gallery.querySelector('.js-gallery-next');

      if (!gallery || !prevBtn || !nextBtn) return;

      const mediaItems = gallery.querySelectorAll('.js-mobile-media');
      const totalItems = mediaItems.length;
      let currentIndex = 0;

      function updateButtonStates() {
        if (currentIndex === 0) {
          prevBtn.disabled = true;
          prevBtn.classList.add('disabled');
        } else {
          prevBtn.disabled = false;
          prevBtn.classList.remove('disabled');
        }

        if (currentIndex === totalItems - 1) {
          nextBtn.disabled = true;
          nextBtn.classList.add('disabled');
        } else {
          nextBtn.disabled = false;
          nextBtn.classList.remove('disabled');
        }
      }

      function scrollToIndex(index) {
        if (index < 0 || index >= totalItems) return;

        const targetMedia = mediaItems[index];
        const scrollLeft = targetMedia.offsetLeft;
        gallery.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });

        currentIndex = index;
        updateButtonStates();
      }

      prevBtn.addEventListener('click', function(e) {
        e.preventDefault();
        if (currentIndex > 0) {
          scrollToIndex(currentIndex - 1);
        }
      });

      nextBtn.addEventListener('click', function(e) {
        e.preventDefault();
        if (currentIndex < totalItems - 1) {
          scrollToIndex(currentIndex + 1);
        }
      });

      let scrollTimeout;
      gallery.addEventListener('scroll', function() {
        clearTimeout(scrollTimeout);

        scrollTimeout = setTimeout(function() {
          const scrollLeft = gallery.scrollLeft;
          const itemWidth = mediaItems[0].offsetWidth;
          const newIndex = Math.round(scrollLeft / itemWidth);

          if (newIndex !== currentIndex && newIndex >= 0 && newIndex < totalItems) {
            currentIndex = newIndex;
            updateButtonStates();
          }
        }, 100);
      }, { passive: true });

      updateButtonStates();
    }
    renderZoomGallery(img) {
      const template = this.querySelector('.js-template-gallery-zoom').content.cloneNode(true);
      for (let imgData of this.state.media) {
        if (imgData.type !== 'video') {
          // Add thumbnails
          const thumbTemplate = template.querySelector('.js-template-zoom-thumb').content.cloneNode(true);
          const thumbImg = thumbTemplate.querySelector('.js-zoom-thumb');
          thumbImg.src = imgData.src;
          template.querySelector('.js-zoom-thumbs-container').appendChild(thumbTemplate);
        }
      }
      // Add main image
      const imgTemplate = template.querySelector('.js-template-zoom-image').content.cloneNode(true);
      const zoomImg = imgTemplate.querySelector('.js-zoom-img');
      // Set main image to clicked image
      zoomImg.dataset.srcset = img.dataset.srcset;
      zoomImg.dataset.sizes = 'auto';
      template.querySelector('.js-zoom-img-container').appendChild(imgTemplate);
      this.querySelector('.js-gallery-desktop-container').appendChild(template);
      this.querySelector('.js-gallery-zoom-container').classList.remove('hidden');
      document.querySelector('body').classList.add('overflow-hidden', 'zoom-enabled');
      this.querySelector('.js-zoom-img').onload = () => {
        this.querySelector('.js-zoom-img').classList.add('loaded');
      }
    }
    handleZoomGallery() {
      const desktopContainer = this.querySelector('.js-gallery-desktop-container');
      if (!desktopContainer || desktopContainer._hasZoomDelegation) return;
      desktopContainer._hasZoomDelegation = true;
      desktopContainer.addEventListener('click', (e) => {
        const mediaImg = e.target.closest('img.js-media');
        if (!mediaImg) return;
        const img = mediaImg.parentNode.querySelector('.js-media');
        this.renderZoomGallery(img);

        const zoomContainer = this.querySelector('.js-gallery-zoom-container');
        if (!zoomContainer) return;

        zoomContainer.addEventListener('click', (ev) => {
          const thumb = ev.target.closest('.js-zoom-thumb');
          if (thumb) {
            this.querySelector('.js-zoom-img').srcset = getSrcset(thumb.src, this.defaultImgSize);
            this.querySelector('.js-zoom-img').src = img.src;
            return;
          }
          if (ev.target.closest('.js-zoom-close')) {
            this.closeZoom();
            return;
          }
          if (ev.target.closest('.js-zoom-img-container') && !ev.target.classList.contains('js-zoom-img')) {
            this.closeZoom();
          }
        });
        zoomContainer.addEventListener('mouseover', (ev) => {
          const thumb = ev.target.closest('.js-zoom-thumb');
          if (thumb) {
            this.querySelector('.js-zoom-img').srcset = getSrcset(thumb.src, this.defaultImgSize);
            this.querySelector('.js-zoom-img').src = thumb.src;
          }
        });
      });
    }
    handleStylistChat() {
      if (this.querySelector('.js-chat-stylist')) {
        this.querySelector('.js-chat-stylist').addEventListener('click', () => {
          document.querySelector('[data-chat-action="openChat"]').click();
        });
      }
      if (window.theme?.chat?.isOnline) {
        if (this.querySelector('.js-chat-stylist-container')) {
          this.querySelector('.js-chat-stylist-container').classList.remove('hidden');
        }
      }
    }
    closeZoom() {
      this.querySelector('.js-gallery-zoom-container').classList.add('hidden');
      document.querySelector('body').classList.remove('overflow-hidden', 'zoom-enabled');
      this.querySelector('.js-gallery-zoom-container').remove();
    }
    afterRender() {
      this.state.firstRender = false;
      this.handleMobileGallery();
      this.handleThumbnails();
      this.handleControls();
      this.handleZoomGallery();
      this.handleStylistChat();
      this.handleModelBtn();
      this.handleModelClicks();
      if (window.theme?.loyalty?.is_rewards_banner_displayed) {
        const parentNode = this.parentNode;
        if (parentNode) parentNode.style.top = '213px';
      }
    }
    handleThumbnails() {
      const thumbnailsEl = this.querySelector('.js-thumbnails');
      if (!thumbnailsEl || thumbnailsEl._hasThumbnailDelegation) return;
      thumbnailsEl._hasThumbnailDelegation = true;
      thumbnailsEl.addEventListener('click', (e) => {
        const thumbnail = e.target.closest('.js-thumbnail');
        if (!thumbnail) return;
        const index = parseInt(thumbnail.dataset.index, 10);
        if (isNaN(index)) return;
        this.state.mediaIndex = index;
        this.renderMedia(this);
        this.updateModelSize(this);
      });
    }
    handleControls() {
      const prevButton = this.querySelector('.js-gallery-prev');
      const nextButton = this.querySelector('.js-gallery-next');
      const updateMediaIndex = (increment) => {
        if (!this.state.mediaIndex && increment < 0) {
          prevButton.setAttribute('disabled', 'disabled');
          return
        }

        this.state.mediaIndex += increment;
        if (!this.state.mediaIndex) {
          prevButton.setAttribute('disabled', 'disabled');
        } else if (this.state.mediaIndex >= this.state.media.length -1) {
          nextButton.setAttribute('disabled', 'disabled');
        } else {
          prevButton.removeAttribute('disabled');
          nextButton.removeAttribute('disabled');
        }

        this.renderMedia(this);
      };
      if (prevButton) {
        prevButton.addEventListener('click', () => updateMediaIndex(-1));
      }
      if (nextButton) {
        nextButton.addEventListener('click', () => updateMediaIndex(1));
      }
    }
    handleModelBtn() {
      this.querySelectorAll('.js-gallery-model-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          this.querySelectorAll('.js-models-container').forEach(container => container.classList.toggle('hidden'));
        });
      });
    }
    handleModelClicks() {
      this.querySelectorAll('.js-model').forEach(model => {
        model.addEventListener('click', () => {
          this.state.activeModelId = model.dataset.id;
          this.closest('component-pdp-product').dispatchEvent(new CustomEvent('update-images', {
            bubbles: true,
            detail: {
              images: this.state.models[model.dataset.id]
            }
          }));
          this.querySelectorAll('.js-gallery-model-select-text').forEach(text => text.innerHTML = 'Change Model Size');
        });
      });
      this.querySelectorAll('.js-models-close').forEach(close => {
        close.addEventListener('click', () => {
          this.querySelectorAll('.js-models-container').forEach(container => container.classList.toggle('hidden'));
        });
      });
    }

    async notifyRebuyViewed() {
      try {
        const visitorID = localStorage.getItem('rebuy_user_uuid') || crypto.randomUUID();
        localStorage.setItem('rebuy_user_uuid', visitorID);

        const queryParams = {
          "key": this.props.rebuyApiKey,
          "subject": "user",
          "verb": "viewed",
          "noun": "product",
          "uuid": visitorID,
          "shopify_product_handle": this.props.productHandle
        }
        await fetch(`${this.props.rebuyEventApiUrl}?${new URLSearchParams(queryParams).toString()}`,
          {
            method: 'POST'
          }
        );
      } catch (e) {
        console.error('Error sending view event to Rebuy', e);
      }
    }

  })
}

// Product info subcomponent
if (!customElements.get('component-pdp-product-info')) {
  customElements.define('component-pdp-product-info', class PDPProductInfo extends PDPProductSubComponent {
    constructor() {
      super();
      this.props = {
        bdEnabled: this.dataset.bdEnabled === 'true',
        showPriceBadge: this.dataset.showPriceBadge,
        priceBadgeSetsTag: this.dataset.priceBadgeSetsTag
      }
      this.state = {};
      if (this.props.bdEnabled) {
        this.props.bdDiscountType = this.dataset.bdDiscountType;
        this.state.bdAmountToSave = this.dataset.bdAmountToSave;
        this.state.bdItemsCount = +this.dataset.bdItemsCount || 0;
        this.props.bdTag = this.dataset.bdTag;
        this.props.bdProductType = this.dataset.bdProductType;
        this.props.bdPdpLabelEmptyTemplate = this.dataset.bdPdpLabelEmptyTemplate || '';
        this.props.bdPdpLabelTemplate = this.dataset.bdPdpLabelTemplate || '';
        this.props.bdPdpLabelAllTiersEnabled = this.dataset.bdPdpLabelAllTiersEnabled === 'true';
        this.props.bdFirstItemDiscount = this.dataset.bdFirstItemDiscount;
        this.props.bdSecondItemDiscount = this.dataset.bdSecondItemDiscount;
        this.props.bdThirdItemDiscount = this.dataset.bdThirdItemDiscount;
        this.props.bdFourthItemDiscount = this.dataset.bdFourthItemDiscount;
        this.props.bdFifthItemDiscount = this.dataset.bdFifthItemDiscount;
      }
    }
    render() {
      const template = this.template.content.cloneNode(true);
      // Set title and reviews
      if (this.getProduct()) {
        if (this.getSiblingColor().eyebrow) {
          // Set eyebrow text
          template.querySelector('.js-eyebrow').textContent = this.getSiblingColor().eyebrow;
          // Set eyebrow collection link
          try {
            const eyebrowCollections = document.querySelector('.js-eyebrow-collections');
            if (eyebrowCollections) {
              const jsonObj = JSON.parse(eyebrowCollections.innerHTML);
              if (Object.keys(jsonObj).length !== 0) {
                const eyebrowKey = Object.keys(jsonObj).find(key => key === this.getSiblingColor().eyebrow.toLowerCase().trim());
                if (jsonObj[eyebrowKey]) {
                  template.querySelector('.js-eyebrow-link').href = jsonObj[eyebrowKey];
                } else {
                  template.querySelector('.js-eyebrow-link').removeAttribute('href');
                }
              }
            }
          } catch (e) {
            console.error('Failed to parse eyebrow collections JSON:', e);
          }
        }
        if (this.getSiblingColor().style) {
          template.querySelector('.js-style').textContent = this.getSiblingColor().style;
        }
        const reviewStars = `<component-review-stars data-reviews='${this.getSiblingColor().reviews_average}' data-total="${this.getSiblingColor().reviews_count}" data-type="product"></component-review-stars>`;
        template.querySelector('.js-review-stars').innerHTML = reviewStars;
      }
      // Set pricing
      if (this.getSiblingColor()) {
        const price = this.getVariant() ? this.getVariant().price : this.getSiblingColor().price;
        const comparePrice = this.getVariant() ? this.getVariant().compare_at_price : this.getSiblingColor().compare_at_price;
        template.querySelector('.js-cost').innerHTML = toMoney(price);
        if (parseInt(comparePrice) > parseInt(price)) {
          template.querySelector('.js-compare-price').innerHTML = toMoney(comparePrice);
          template.querySelector('.js-cost').classList.add('cost--sale');
          template.querySelector('.js-compare-price').classList.remove('hidden');
        } else {
          template.querySelector('.js-compare-price').classList.add('hidden');
          template.querySelector('.js-cost').classList.remove('cost--sale');
        }
        this.renderPriceBadge(template, comparePrice, price);
        // Afterpay
        if (template.querySelector('.js-afterpay')) {
          if ((price) > parseFloat(template.querySelector('.js-afterpay').dataset.minPrice)) {
            template.querySelector('.js-afterpay-price').innerHTML = toMoney(price / parseInt(template.querySelector('.js-afterpay-available').dataset.installments));
            template.querySelector('.js-afterpay-available').classList.remove('hidden');
            template.querySelector('.js-afterpay-unavailable').classList.add('hidden');
          } else {
            template.querySelector('.js-afterpay-available').classList.add('hidden');
            template.querySelector('.js-afterpay-unavailable').classList.remove('hidden');
          }
        }
      }
      if (this.props.bdEnabled) {
        this.renderBundleDiscountPromo(template);
      }
      if (this.props.hydration && this.querySelector('.js-template')) this.querySelector('.js-template').remove();
      this.append(template);
      this.afterRender();
    }
    afterRender() {
      this.querySelector('.js-review-stars').addEventListener('click', () => {
        document.dispatchEvent(new CustomEvent('scroll-to-reviews'));
        if (document.querySelector('.js-product-reviews-desktop')) {
          document.querySelector('.js-product-reviews-desktop').scrollIntoView();
        }
      });
      if (this.props.bdEnabled) {
        window.addEventListener('update-bd-text', (data) => {
          if (data.detail && data.detail.amountToSave) {
            this.state.bdAmountToSave = data.detail.amountToSave;
            this.state.bdItemsCount = +data.detail.bdItemsCount;
          }
          this.renderBundleDiscountPromo(this);
        });
      }
    }
    renderPriceBadge(template, comparePrice, price) {
      if (this.props.showPriceBadge !== 'none' && template.querySelector('.js-template-price-badge')) {
        const priceBadgeTemplate = template.querySelector('.js-template-price-badge').content.cloneNode(true);
        if (comparePrice > price) {
          priceBadgeTemplate.querySelectorAll('.js-price-badge-percent').forEach(percent => percent.innerHTML = `${Math.floor((1 - (price / comparePrice)) * 100)}%`);
          if (this.props.showPriceBadge === 'all' || (this.props.showPriceBadge === 'sets_only' && this.getSiblingColor().tags.includes(this.props.priceBadgeSetsTag))) {
            if(this.querySelector('.js-pdp-price-right-hand-side')) {
              template.querySelector('.js-pdp-price-right-hand-side').appendChild(priceBadgeTemplate);
            } else {
              template.querySelector('.js-price-container').appendChild(priceBadgeTemplate);
            }
          }
        }
      }
    }
    renderBundleDiscountPromo(template) {

      const bdTextEl = template.querySelector('.js-bd-text');

      if(this.props.bdPdpLabelAllTiersEnabled) {

        if(bdTextEl) {

          bdTextEl.innerHTML = '';
          let firstItemDiscount = this.props.bdFirstItemDiscount;
          let secondItemDiscount = this.props.bdSecondItemDiscount;
          let thirdItemDiscount = this.props.bdThirdItemDiscount;
          let fourthItemDiscount = this.props.bdFourthItemDiscount;
          let fifthItemDiscount = this.props.bdFifthItemDiscount;
          let template = this.props.bdPdpLabelTemplate ? this.props.bdPdpLabelTemplate : this.props.bdPdpLabelEmptyTemplate ? this.props.bdPdpLabelEmptyTemplate : '';

          if(template && this.getSiblingColor().tags.includes(this.props.bdTag)) {

            let hasAvailableTiers = false;

            // Add all tiers to the template
            if(firstItemDiscount && firstItemDiscount != "0") {
              hasAvailableTiers = true;
              bdTextEl.innerHTML += template.replace('#product_type', this.props.bdProductType).replace('#tier_number', 1).replace('#tier_ordinal', 'first').replace('#discount', this.props.bdDiscountType === 'percentage' ? `${firstItemDiscount}%` : toMoney(firstItemDiscount)) + '<br>';
            }
            if(secondItemDiscount && secondItemDiscount != "0") {
              hasAvailableTiers = true;
              bdTextEl.innerHTML += template.replace('#product_type', this.props.bdProductType).replace('#tier_number', 2).replace('#tier_ordinal', 'second').replace('#discount', this.props.bdDiscountType === 'percentage' ? `${secondItemDiscount}%` : toMoney(secondItemDiscount)) + '<br>';
            }
            if(thirdItemDiscount && thirdItemDiscount != "0") {
              hasAvailableTiers = true;
              bdTextEl.innerHTML += template.replace('#product_type', this.props.bdProductType).replace('#tier_number', 3).replace('#tier_ordinal', 'third').replace('#discount', this.props.bdDiscountType === 'percentage' ? `${thirdItemDiscount}%` : toMoney(thirdItemDiscount)) + '<br>';
            }
            if(fourthItemDiscount && fourthItemDiscount != "0") {
              hasAvailableTiers = true;
              bdTextEl.innerHTML += template.replace('#product_type', this.props.bdProductType).replace('#tier_number', 4).replace('#tier_ordinal', 'fourth').replace('#discount', this.props.bdDiscountType === 'percentage' ? `${fourthItemDiscount}%` : toMoney(fourthItemDiscount)) + '<br>';
            }
            if(fifthItemDiscount && fifthItemDiscount != "0") {
              hasAvailableTiers = true;
              bdTextEl.innerHTML += template.replace('#product_type', this.props.bdProductType).replace('#tier_number', 5).replace('#tier_ordinal', 'fifth').replace('#discount', this.props.bdDiscountType === 'percentage' ? `${fifthItemDiscount}%` : toMoney(fifthItemDiscount)) + '<br>';
            }

            if(hasAvailableTiers) {
              bdTextEl.classList.remove('hidden');
            }

          } else {
            bdTextEl.classList.add('hidden');
          }
        }
      }
      else {

        const amount = this.state.bdAmountToSave;
        const count = this.state.bdItemsCount + 1;
        const ordinal = count > 4 ? 'fifth' : count === 4 ? 'fourth' : count === 3 ? 'third' : count === 2 ? 'second' : 'first';
        if (bdTextEl && amount) {
          if (amount === 'discount_reached' || isFinalSale(this.getSiblingColor().tags) || !this.getSiblingColor().tags.includes(this.props.bdTag)) {
            bdTextEl.innerHTML = '';
            bdTextEl.classList.add('hidden');
          } else if (count < 2 && this.props.bdPdpLabelEmptyTemplate) {
            bdTextEl.innerHTML = this.props.bdPdpLabelEmptyTemplate.replace('#product_type', this.props.bdProductType).replace('#tier_number', count).replace('#tier_ordinal', ordinal).replace('#discount', this.props.bdDiscountType === 'percentage' ? `${amount}%` : toMoney(amount));
            bdTextEl.classList.remove('hidden');
          } else if (this.props.bdPdpLabelTemplate) {
            bdTextEl.innerHTML = this.props.bdPdpLabelTemplate.replace('#product_type', this.props.bdProductType).replace('#tier_number', count).replace('#tier_ordinal', ordinal).replace('#discount', this.props.bdDiscountType === 'percentage' ? `${amount}%` : toMoney(amount));
            bdTextEl.classList.remove('hidden');
          } else {
            bdTextEl.classList.add('hidden');
          }
        }
      }
    }
  });
}

// Product variants (color / size) subcomponent
if (!customElements.get('component-pdp-product-variants')) {
  customElements.define('component-pdp-product-variants', class PDPProductVariants extends PDPProductSubComponent {
    constructor() {
      super();

      this.props.replaceUrl = this.dataset.replaceUrl && this.dataset.replaceUrl === 'true' ? true : false;
      this.props.classicTitle = this.dataset.classicTitle;
      this.props.limitedTitle = this.dataset.limitedTitle;
      this.props.hasCollectionMap1 = this.dataset.collectionMap1 === 'true';
      this.props.hasCollectionMap2 = this.dataset.collectionMap2 === 'true';
      this.props.hasCollectionMap3 = this.dataset.collectionMap3 === 'true';
      this.props.collectionMap1Title = this.dataset.collectionMap1Title;
      this.props.collectionMap2Title = this.dataset.collectionMap2Title;
      this.props.collectionMap3Title = this.dataset.collectionMap3Title;
      this.props.isLanding = this.dataset.isLanding === 'true';
      this.props.inventoryQuantityThreshold = this.dataset.inventoryQuantityThreshold;
      this.props.inventoryQuantityMessage = this.dataset.inventoryQuantityMessage;
      this.props.isPreciseSizing = this.dataset.isPreciseSizing;

      this.state = {
        disabledBandSize: null,
        bandSize: null,
        cupSize: null,
        formFitBandSize: '',
        formFitCupSize: '',
        formFitSize: '',
        formFitSizeMessage: '',
        priveWidgetLoaded: false,
        currentProductId: null,
      };
      this.isCanada = window.Shopify.country === 'CA';
      this.acceleratedCheckoutButtons = null;
    }
    render() {
      const template = this.template.content.cloneNode(true);
      this.setDefaultSize();
      this.renderColors(template);
      if (this.hasBandCup()) {
        this.renderSizesNewDesign(template, 'band');
        this.renderSizesNewDesign(template, 'cup');
      } else {
        this.renderSizesNewDesign(template, 'alpha');
      }
      if (template.querySelector('.js-size-chart-design-head')) {
        this.renderSizeChart(template);
      }
      this.renderOutOfStock(template);
      if (this.props.inventoryQuantityThreshold !== '0') {
        this.renderInventoryMessage(template);
      }
      this.renderFinalSale(template);
      this.renderPerfectFit(template);
      if (this.querySelector('.js-skeleton')) this.querySelector('.js-skeleton').remove();
      this.append(template);
      this.afterRender();
    }
    setDefaultSize() {
      if (this.dom.parentComponent.state.selectedSize) {
        const siblingColorSizes = Object.keys(this.getSiblingColor().variants);
        if (siblingColorSizes.includes(this.dom.parentComponent.state.selectedSize)) {
          this.state.bandSize = this.dom.parentComponent.state.selectedSize ? this.dom.parentComponent.state.selectedSize.substring(0, 2) : '';
          this.state.cupSize = this.dom.parentComponent.state.selectedSize ? this.dom.parentComponent.state.selectedSize.substring(2) : '';
        }
      }
    }
    renderColors(template) {
      // Supports ability to merge three collections into one PDP
      const colors = Object.values(this.getProduct().colors);
      // Render main colors
      if (this.props.hasCollectionMap2 || this.props.hasCollectionMap3) {
        this.addColorCategory(template, 'colors');
        this.addNewColorCategory(template, 'colors', this.props.collectionMap1Title);
        const mainColors = this.props.hasCollectionMap1 ? colors.filter(color => color.parent_collection_type === 'collection_map_1') : colors;
        for (const color of mainColors) {
          this.addColor(color.color, color.color_swatch_name, template, 'colors');
        }
      } else {
        const classicColors = colors.filter(color => !color.tags.includes('limited colors'));
        const limitedColors = colors.filter(color => color.tags.includes('limited colors'));
        if (classicColors.length > 0 && limitedColors.length > 0) {
          this.addColorCategory(template, 'classic');
          this.addColorCategory(template, 'limited');
          for (const color of classicColors) {
            this.addColor(color.color, color.color_swatch_name, template, 'classic');
          }
          for (const color of limitedColors) {
            this.addColor(color.color, color.color_swatch_name, template, 'limited');
          }

          // Display all colors on frontend, including limited colors
          let colorContainers = template.querySelectorAll('.colors-container .color-group:not(:first-of-type)');
          colorContainers.forEach(container => {
            container.style.display = 'block';
          });

        } else {
          this.addColorCategory(template, 'all');
          for (const color of colors) {
            this.addColor(color.color, color.color_swatch_name, template, 'all');
          }
        }
      }
      // Render collection map 2 colors
      if (this.props.hasCollectionMap2) {
        this.addNewColorCategory(template, 'colors-2', this.props.collectionMap2Title);
        this.addColorCategory(template, 'colors-2');
        const colors2 = colors.filter(color => color.parent_collection_type === 'collection_map_2');
        for (const color of colors2) {
          this.addColor(color.color, color.color_swatch_name, template, 'colors-2');
        }
      }
      // Render collection map 3 colors
      if (this.props.hasCollectionMap3) {
        this.addNewColorCategory(template, 'colors-3', this.props.collectionMap3Title);
        this.addColorCategory(template, 'colors-3');
        const colors3 = colors.filter(color => color.parent_collection_type === 'collection_map_3');
        for (const color of colors3) {
          this.addColor(color.color, color.color_swatch_name, template, 'colors-3');
        }
      }
      this.closest('component-pdp-product').dispatchEvent(new CustomEvent('colors-rendered'));
    }
    addNewColorCategory(template, category, categoryTitle) {
      const categoryTemplate = template.querySelector('.js-template-new-colors').content.cloneNode(true);
      const listCategories = ['all', 'colors', 'colors-2', 'colors-3'];
      categoryTemplate.querySelector('.js-new-colors').classList.add(`js-${category}-colors-news`);

      if (category === 'colors')
        categoryTemplate.querySelector('.js-new-colors').classList.add(`active-category`);

      if (!listCategories.includes(category)) return;

      categoryTemplate.querySelector('.title-new-color').innerHTML = categoryTitle;
      categoryTemplate.querySelector('.js-new-colors').addEventListener('click', _ => {
        let color;
        const stateColor = this.dom.parentComponent.state?.colorCategory;
        if (stateColor?.[category]) {
          color = stateColor?.[category].color;
        } else {
          const inputs = this.querySelectorAll(`.color-group-${category} .js-input`);
          color = inputs[0].value;
        }

        this.handleChangeUrl(color);

        setTimeout(() => {
          this.sendColorChangeGTMData(color);
          this.handleBundleButton(color);
          this.handleBundleButtonDiscountKit(color);
          this.updateLoyaltyBanner();

          this.dispatchEvent(new CustomEvent('set-state', {
            bubbles: true,
            detail: {
              colorCategory: {
                [category]: {
                  title: category,
                  color: color,
                },
                activeCategory: category,
              },
              selectedColor: color,
            }
          }));
        }, 0);
      });
      template.querySelector('.new-colors-container').append(categoryTemplate);
    }
    addColorCategory(template, category) {
      const categoryTemplate = template.querySelector('.js-template-color-category').content.cloneNode(true);
      categoryTemplate.querySelector('.js-category-colors').classList.add(`js-${category}-colors`);
      categoryTemplate.querySelector('.js-active-category-color').classList.add(`js-active-${category}-color`);
      categoryTemplate.querySelector('.color-group').classList.add(`color-group-${category}`);
      categoryTemplate.querySelector('.color-group').setAttribute('data-color-group', category);

      let categoryTitle;
      if (category === 'all') {
        //categoryTitle = this.props.collectionMap1Title;
        categoryTitle = 'Color';
      } else if (category === 'classic') {
        //categoryTitle = this.props.classicTitle;
        categoryTitle = 'Color';
      } else if (category === 'limited') {
        categoryTitle = this.props.limitedTitle;
      } else if (category === 'colors') {
        //categoryTitle = `${this.props.collectionMap1Title}:`;
        categoryTitle = 'Color';
      } else if (category === 'colors-2') {
        //categoryTitle = `${this.props.collectionMap2Title}:`;
        categoryTitle = 'Color';
      } else if (category === 'colors-3') {
        //categoryTitle = `${this.props.collectionMap3Title}:`;
        categoryTitle = 'Color';
      }
      categoryTemplate.querySelector('.js-category').innerHTML = categoryTitle;
      template.querySelector('.js-colors').append(categoryTemplate);
    }
    addColor(color, colorSwatchName, template, category) {
      const colorTemplate = template.querySelector('.js-template-color').content.cloneNode(true);
      if (window.theme.colors[colorSwatchName]) {
        // Set CSS custom properties for swatch styling
        if (window.theme.colors[colorSwatchName].image) {
          colorTemplate.querySelector('.js-label').style.setProperty('--swatch-image', `url(${window.theme.colors[colorSwatchName].image}`);
        }
        colorTemplate.querySelector('.js-label').style.setProperty('--swatch-color', window.theme.colors[colorSwatchName].color);
        colorTemplate.querySelector('.js-label').style.setProperty('--swatch-border-color', '#fff');
      }
      colorTemplate.querySelector('.js-label').setAttribute('for', `color_${color}_${this.dom.parentComponent.props.pdpId}`);
      colorTemplate.querySelector('.js-input').value = color;
      colorTemplate.querySelector('.js-input').id = `color_${color}_${this.dom.parentComponent.props.pdpId}`;
      if (this.dom.parentComponent.state.selectedColor === color) {
        colorTemplate.querySelector('.js-input').checked = true;
        if (window.theme.colors[colorSwatchName]) {
          template.querySelector(`.js-active-${category}-color`).innerHTML = window.theme.colors[colorSwatchName].title;
        }
      }
      // Add sale text if on sale
      const templateSaleTextEl = colorTemplate.querySelector('.js-template-color-text');
      if (templateSaleTextEl) {
        const saleTextTemplate = colorTemplate.querySelector('.js-template-color-text').content.cloneNode(true);
        if (!!this.getProduct().colors[color].pdp_color_label) {
          saleTextTemplate.querySelector('.color-label-text').innerHTML = this.getProduct().colors[color].pdp_color_label;
        } else if (parseInt(this.getProduct().colors[color].compare_at_price) > parseInt(this.getProduct().colors[color].price) && !this.getProduct().colors[color].tags.includes('no-sale-text')) {
          saleTextTemplate.querySelector('.color-label-text').innerHTML = 'Sale';
        }
        colorTemplate.querySelector('.js-color-container').appendChild(saleTextTemplate);
      }
      // Handle colors that are out of stock for selected size
      let availableSizes = this.getProduct().colors[color].availableSizes;
      if (this.hasBandCup() && (this.state.bandSize || this.state.cupSize)) {
        // Filter by band size
        if (this.state.bandSize) availableSizes = availableSizes.filter(size => size.substring(0, 2) === this.state.bandSize)
        if (this.state.cupSize) {
          if (this.state.cupSize.includes('-') || availableSizes.some(size => size.includes('-'))) {
            // Handle cases where product cup size includes half size (ex: "C-C½", Kinetic Impact Sports Bra)
            availableSizes = availableSizes.filter(size => size.substring(2).includes(this.state.cupSize) || this.state.cupSize.includes(size.substring(2)));
          } else {
            availableSizes = availableSizes.filter(size => size.substring(2) === this.state.cupSize)
          }
        }
      } else {
        // Handle Alpha group
        if (this.dom.parentComponent.state.selectedSize) {
          availableSizes = availableSizes.filter(size => size === this.dom.parentComponent.state.selectedSize);
        }
      }
      // Handle colors with no available sizes
      colorTemplate.querySelector('.js-name').innerHTML = color;
      if (availableSizes.length < 1) {
        colorTemplate.querySelector('.js-label').classList.add('color-label--out-of-stock')
      }
      template.querySelector(`.js-${category}-colors`).appendChild(colorTemplate);
    }
    renderSizesNewDesign(template, name) {
      const config = {
        band: {
          subStart: 0,
          subEnd: 2,
          label: 'Band',
          template: 'sizes-new-design-container-template',
        },
        cup: {
          subStart: 2,
          subEnd: undefined,
          label: 'Cup',
          template: 'sizes-new-design-container-template-two',
        },
        alpha: {
          subStart: 0,
          subEnd: undefined,
          label: 'Size',
          template: 'sizes-new-design-container-template',
        }
      }

      const mapSize = {
        'E': 'E (DD)',
        'E½': 'E½ (DD½)',
        'E-E½': 'DD-DD½',
        'F': 'F (DDD)',
        'G': 'G (DDDD)',
        'H': 'H (DDDDD)',
        'I': 'I (DDDDDD)'
      }

      const configItem = config[name];
      if (!configItem) return;
      const product = this.getProduct();
      const _template = template.querySelector(`.${configItem.template}`).content.cloneNode(true);
      const sizeTemplate = _template.querySelector(`.${configItem.template}`);
      sizeTemplate.classList.add(`js-sizes--${name}`)
      const sizesSelect = sizeTemplate.querySelector('.sizes-title-select');
      let sizes = Object.keys(this.getSiblingColor().variants);

      if (name === 'cup' && this.state.bandSize)
        sizes = sizes.filter(size => size.substring(0, configItem.subStart) === this.state.bandSize);

      sizes = sizes.map(size => size.substring(configItem.subStart, configItem.subEnd));
      sizes = [...new Set(sizes)];
      if (name !== 'alpha') {
        sizes.sort();
        sizes.sort(a => a === 'AA' ? -1 : 0);
      }
      if (!sizes) return;

      const _label = name === 'alpha' && this.props.isPreciseSizing ? 'Select a Precise Size™' : configItem.label;
      sizeTemplate.querySelector('.sizes-title').textContent = _label;
      sizes.map(size => {
        const sizesLabel = sizeTemplate.querySelector('.js-sizes-label').content.cloneNode(true);
        const input = sizesLabel.querySelector('.js-size-input');
        const label = sizesLabel.querySelector('.js-size-label');
        const _label = mapSize[size] || size;
        const price = parseFloat(_label.slice(1));
        const canadaPrice = price * this.props.cadExchangeRate;
        const moneyPrice = `$${Math.ceil(canadaPrice.toFixed(2))}`;

        input.name = name
        input.id = `size-${name}-${size}`;
        input.value = size;
        input.dataset.size = size;
        input.dataset.name = name;
        label.textContent = (this.isCanada && product.type?.toLowerCase()?.includes('gift card')) ? moneyPrice : _label;

        if (name === 'band' && size === this.state.bandSize) {
          input.checked = true;
          sizesSelect.textContent = _label;
        } else if (name === 'cup' && size === this.state.cupSize) {
          input.checked = true;
          sizesSelect.textContent = _label;
        } else if (name === 'alpha' && size === this.dom.parentComponent.state.selectedSize) {
          input.checked = true;
          sizesSelect.textContent = name === 'alpha' && this.props.isPreciseSizing ? '' : _label;
        }

        if (name === 'band') {
          const availableSizes = this.getSiblingColor().availableSizes.filter(_size =>  _size.substring(0, 2) === size);
          if (!availableSizes.length) sizesLabel.querySelector('.js-sizes-label').classList.add('disabled');
        }

        if (name === 'cup' && this.state.bandSize ) {
          const availableSizes = this.getSiblingColor().availableSizes.filter(size => size.substring(0, 2) === this.state.bandSize);
          const availableCupSizes = availableSizes.map(size => size.substring(2));

          if (availableCupSizes.indexOf(size) === -1) {
            sizesLabel.querySelector('.js-sizes-label').classList.add('disabled');
          }
        }

        if (name === 'alpha') {
          if (this.getSiblingColor().availableSizes.indexOf(size) === -1) {
            sizesLabel.querySelector('.js-sizes-label').classList.add('disabled');
          }
        }

        sizeTemplate.querySelector('.container-js-sizes-element').appendChild(sizesLabel);
      })

      if (this.props.isPreciseSizing && this.state.formFitSizeMessage) {
        // sizeTemplate.querySelector('.js-sizes-message-other').classList.remove('hidden');
        sizeTemplate.querySelector('.js-sizes-message-other').textContent = this.state.formFitSizeMessage;
      }

      template.querySelector('.sizes-new-design').appendChild(sizeTemplate);
    }
    renderSizes(template, name, subStart, subEnd) {
      const product = this.getProduct();
      const sizeTemplate = template.querySelector('.js-template-size').content.cloneNode(true);
      sizeTemplate.querySelector('.js-size').classList.add(`js-size-${name}`);
      let sizes = Object.keys(this.getSiblingColor().variants);
      if (name === 'cup' && this.state.bandSize) {
        sizes = sizes.filter(size => size.substring(0, 2) === this.state.bandSize);
      }
      sizes = sizes.map(size => size.substring(subStart, subEnd));
      // Get unique values
      sizes = [...new Set(sizes)];
      // Sort ascending order for bands and cups
      if (name !== 'alpha') {
        sizes.sort();
        sizes.sort(a => {
          if (a === 'AA') {
            return -1;
          }
          return 0;
        });
      }
      const defaultOptionTemplate = sizeTemplate.querySelector('.js-template-size-option').content.cloneNode(true);
      const defaultOption = defaultOptionTemplate.querySelector('.js-size-option');
      let defaultLabel = name === 'alpha' ? 'Size' : name;
      defaultLabel = defaultLabel[0].toUpperCase() + defaultLabel.slice(1);
      defaultOption.innerHTML = defaultLabel;
      defaultOption.disabled = true;
      defaultOption.selected = true;
      sizeTemplate.querySelector('.js-size-dropdown').appendChild(defaultOptionTemplate);
      for (let size of sizes) {
        let label = size;
        if (size === 'E') label += ' (DD)';
        if (size === 'E½') label += ' (DD½)';
        if (size === 'E-E½') label += ' (DD-DD½)';
        if (size === 'F') label += ' (DDD)';
        if (size === 'G') label += ' (DDDD)';
        if (size === 'H') label += ' (DDDDD)';
        if (size === 'I') label += ' (DDDDDD)';
        const optionTemplate = sizeTemplate.querySelector('.js-template-size-option').content.cloneNode(true);
        const price = parseFloat(label.slice(1));
        const canadaPrice = price * this.props.cadExchangeRate;
        const moneyPrice = `$${Math.ceil(canadaPrice.toFixed(2))}`;
        optionTemplate.querySelector('.js-size-option').innerHTML = (this.isCanada && product.type?.toLowerCase()?.includes('gift card')) ? moneyPrice : label;
        optionTemplate.querySelector('.js-size-option').dataset.size = size;
        optionTemplate.querySelector('.js-size-option').dataset.name = name;
        // Set active size from state
        let populateSize = false;
        if (name === 'band' && size === this.state.bandSize) {
          populateSize = true;
        } else if (name === 'cup' && size === this.state.cupSize) {
          populateSize = true;
        } else if (name === 'alpha' && size === this.dom.parentComponent.state.selectedSize) {
          populateSize = true;
        }
        if (populateSize) {
          optionTemplate.querySelector('.js-size-option').selected = true;
        }
        // Handle out of stock
        let outOfStock;
        if (name === 'cup' && this.state.bandSize) {
          const availableSizes = this.getSiblingColor().availableSizes.filter(size => size.substring(0, 2) === this.state.bandSize);
          const availableCupSizes = availableSizes.map(size => size.substring(2));
          if (availableCupSizes.indexOf(size) === -1) {
            outOfStock = true;
          }
        }
        if (name === 'alpha') {
          if (this.getSiblingColor().availableSizes.indexOf(size) === -1) {
            outOfStock = true;
          }
        }
        if (outOfStock) {
          optionTemplate.querySelector('.js-size-option').classList.add('out-of-stock-option');
        }
        // Add option to options list
        sizeTemplate.querySelector('.js-size-dropdown').appendChild(optionTemplate);
      }
      template.querySelector('.js-sizes').appendChild(sizeTemplate);
    }
    renderSizeChart(template) {
      const sizeChartTemplate = template.querySelector('.js-template-size-chart').content.cloneNode(true);
      sizeChartTemplate.querySelector('component-product-size-chart').dataset.type = this.getProduct().type;
      sizeChartTemplate.querySelector('component-product-size-chart').dataset.title = this.getProduct().title;
      sizeChartTemplate.querySelector('component-product-size-chart').dataset.vendor = this.getProduct().vendor;
      sizeChartTemplate.querySelector('component-product-size-chart').dataset.tags = this.getProduct().tags;
      sizeChartTemplate.querySelector('component-product-size-chart').dataset.id = this.getSiblingColor().id;
      template.querySelector('.js-size-chart-design-head').appendChild(sizeChartTemplate);
    }
    renderOutOfStock(template) {
      if (this.isOutOfStock() || this.getSiblingColor().tags.includes('__label: Coming Soon') || this.state.disabledBandSize) {
        // If size is unavailable, show back in stock widget or message
        let showBIS = true;
        if (this.getSiblingColor().tags.includes('bis-hidden') || this.getSiblingColor().tags.includes('bis-paused')) {
          showBIS = false;
        }
        if(this.getSiblingColor().tags.includes('__label: Coming Soon')) {
          showBIS = true;
        }
        if (showBIS && template.querySelector('.js-back-in-stock') && this.getVariant()) {
          template.querySelector('.wrapper .sizes-title-message').classList.remove('hidden');
          template.querySelector('.js-add-to-bag').disabled = false;
          template.querySelector('.js-add-to-bag').innerHTML = template.querySelector('.js-add-to-bag').dataset.waitlistText || 'Join Waitlist';
          template.querySelector('.js-add-to-bag').dataset.waitlistActive = 'true';
          template.querySelector('.js-add-to-bag-bundle-button').disabled = true;
          template.querySelector('.js-add-to-bag-bundle-button').classList.add('hidden');
          template.querySelector('.js-add-to-bag-bundle-discount-button').disabled = true;
          template.querySelector('.js-add-to-bag-bundle-discount-button').classList.add('hidden');
        } else {
          template.querySelector('.wrapper .sizes-title-message').classList.remove('hidden');
          template.querySelector('.js-add-to-bag').disabled = true;
          template.querySelector('.js-add-to-bag').dataset.waitlistActive = 'false';
          template.querySelector('.js-add-to-bag-bundle-button').disabled = true;
          template.querySelector('.js-add-to-bag-bundle-discount-button').disabled = true;
        }
      } else {
        template.querySelector('.wrapper .sizes-title-message').classList.add('hidden');
        template.querySelector('.js-add-to-bag').disabled = false;
        template.querySelector('.js-add-to-bag').dataset.waitlistActive = 'false';
        template.querySelector('.js-add-to-bag-bundle-button').disabled = false;
        template.querySelector('.js-add-to-bag-bundle-discount-button').disabled = false;

        const addToBagBtn = template.querySelector('.js-add-to-bag');
        const hasSize = !!this.dom.parentComponent.state.selectedSize;
        addToBagBtn.innerHTML = hasSize
          ? addToBagBtn.dataset.addToBagText
          : (addToBagBtn.dataset.selectSizeText || 'Select Size');
      }
    }
    renderInventoryMessage(template) {
      if (this.getVariant() && !this.isOutOfStock() && this.getVariant().inventory_management === 'shopify' && this.getVariant().inventory_policy === 'deny' && Number(this.getVariant().inventory_quantity) < Number(this.props.inventoryQuantityThreshold)) {
        template.querySelector('.js-sizes-inventory-message').textContent = this.props.inventoryQuantityMessage.replace('#', this.getVariant().inventory_quantity);
        template.querySelector('.js-sizes-inventory-message').classList.remove('hidden');
      } else {
        template.querySelector('.js-sizes-inventory-message').classList.add('hidden');
      }
      if (this.getSiblingColor().tags.includes('__label: Coming Soon')) {
        template.querySelector('.js-sizes-inventory-message').classList.add('hidden');
      }
    }
    renderFinalSale(template) {
      if (!this.isOutOfStock() && isFinalSale(this.getSiblingColor().tags)) {
        template.querySelector('.sizes-title-message.js-final-sale').classList.remove('hidden');
      } else {
        template.querySelector('.sizes-title-message.js-final-sale').classList.add('hidden');
      }

      if (this.state.disabledBandSize) {
        template.querySelector('.sizes-title-message.js-final-sale').classList.add('hidden');
      }
    }
    renderPerfectFit(template) {
      if (!this.props.isLanding && template.querySelector('.js-perfect-fit')) {
        if (isFinalSale(this.getSiblingColor().tags)) {
          template.querySelector('.js-perfect-fit').classList.add('hidden');
        } else {
          template.querySelector('.js-perfect-fit').classList.remove('hidden');
        }
      }
    }
    updateLoyaltyBanner() {
      document.dispatchEvent(new CustomEvent('pdp-loyalty-banner-updated', { detail: { price: this.getVariant()?.price || this.getProduct()?.price * 100 }}));
    }
    handleBundleButtonDiscountKit(color) {
      let bundleButtonEl = this.querySelector('.js-add-to-bag-bundle-discount-button');
      let bundleSaveTextEl = this.querySelector('.js-bundle-save-text');

      if (bundleButtonEl) {
        const freshBtn = bundleButtonEl.cloneNode(true);
        bundleButtonEl.replaceWith(freshBtn);
        bundleButtonEl = freshBtn;
        const bbPdpEnabled = bundleButtonEl.dataset.bbPdpEnabled === 'true';
        const bbParamEnabled = bundleButtonEl.dataset.bbParamEnabled === 'true';
        const bbId = bundleButtonEl.dataset.bbId || '';
        let tags = this.dom.parentComponent.state.product.colors[color].tags;

        // Check if is CA
        let isCa = window.Shopify.country === 'CA';
        let dkBBDiscountTag = isCa ? window.cart_settings.dkBBDiscountTagCa : window.cart_settings.dkBBDiscountTag;

        // If US I need to avoid the tag 'bundle-discount-button-ca-', checking is the tag does not contain 'bundle-discount-button-ca-'
        if (!isCa) {
          tags = tags.filter(tag => !tag.includes(window.cart_settings.dkBBDiscountTagCa));
        }

        const tag = tags.find(tag => tag.includes(dkBBDiscountTag));

        if (bbPdpEnabled && tag) {
          const quantityPriceText = tag.split(dkBBDiscountTag)[1];
          const parsed = parseDkPdpBundleDiscountKitSuffix(quantityPriceText);
          if (!parsed) {
            bundleButtonEl.classList.add('hidden');
            bundleSaveTextEl ? bundleSaveTextEl.classList.add('hidden') : null;
          } else {
          const { quantity, priceUsd, priceCad } = parsed;
          let textContent;
          if (parsed.isPercent) {
            textContent = 'Bundle & Save';
          } else if (window.Shopify.country === 'CA') {
            textContent = `${quantity} for ${toMoney(priceCad)} ${window.Shopify.currency.active}`;
          } else {
            textContent = `${quantity} for ${toMoney(priceUsd)}`;
          }
          bundleButtonEl.dataset.addToBagText = textContent;
          bundleButtonEl.dataset.bbQuantity = quantity;
          bundleButtonEl.dataset.bbPrice = priceUsd;
          bundleButtonEl.dataset.bbPriceCa = priceCad;
          if (bbParamEnabled && bbId) {
            const searchParams = new URLSearchParams(window.location.search);
            const bbCookie = jsCookie.get('_tl_bb_id');
            const bundleButtonParamEnabled = searchParams.get('bundleButton') || bbCookie;
            if (bundleButtonParamEnabled === bbId) {
              bundleButtonEl.textContent = textContent;
              bundleButtonEl.classList.remove('hidden');
              bundleSaveTextEl ? bundleSaveTextEl.classList.remove('hidden') : null;
              updateDkBundleSavePercentPdpCopy(bundleSaveTextEl, quantityPriceText, parsed);
              if (!bbCookie || (bbCookie && bbCookie !== bundleButtonParamEnabled)) window.setCookie('_tl_bb_id', bbId, { expires: 1 });
            } else {
              bundleButtonEl.classList.add('hidden');
              bundleSaveTextEl ? bundleSaveTextEl.classList.add('hidden') : null;
              jsCookie.remove('_tl_bb_id');
            }
          } else {
            bundleButtonEl.textContent = textContent;
            bundleButtonEl.classList.remove('hidden');
            bundleSaveTextEl ? bundleSaveTextEl.classList.remove('hidden') : null;
            updateDkBundleSavePercentPdpCopy(bundleSaveTextEl, quantityPriceText, parsed);
          }
          }
        } else {
          bundleButtonEl.classList.add('hidden');
          bundleSaveTextEl ? bundleSaveTextEl.classList.add('hidden') : null;
        }

        bundleButtonEl.addEventListener('click', async (event) => {
          event.preventDefault();

          try {
            const variant = this.getVariant();

            if (!variant) {
              throw new Error('No variant selected');
            }

            bundleButtonEl.innerHTML = bundleButtonEl.dataset.loadingText;
            bundleButtonEl.classList.add('add-to-bag-btn--loading');

            const bundleButtonId = keygen('bundle-button');
            const product = this.getProduct().colors[this.dom.parentComponent.state.selectedColor];
            const payload = {
              id: variant.id,
              quantity: +bundleButtonEl.dataset.bbQuantity,
              properties: {
                _dkQuantity: +bundleButtonEl.dataset.bbQuantity,
                _dkPrice: +bundleButtonEl.dataset.bbPrice,
                _dkPriceCa: +bundleButtonEl.dataset.bbPriceCa,
                _tags: filterProductTags(product.tags),
                _isBundleButtonDiscountKit: true,
                _hasDiscountKitApplied: true
              }
            };

            const price = variant.price;
            const compareAtPrice = variant.compare_at_price;
            if (window.Shopify.country === 'CA' && compareAtPrice && compareAtPrice > price) {
              payload.properties._compareAtPrice = Math.trunc((compareAtPrice / 100) / window.i18n_cad_exchange_rate) * 100;
              payload.properties._compareAtPriceCa = compareAtPrice;
            } else if (compareAtPrice && compareAtPrice > price) {
              payload.properties._compareAtPrice = compareAtPrice;
              payload.properties._compareAtPriceCa = Math.ceil((compareAtPrice / 100) * window.i18n_cad_exchange_rate) * 100;
            }

            await window.cartManager.addItem(payload);

            bundleButtonEl.innerHTML = bundleButtonEl.dataset.addToBagText;
            bundleButtonEl.classList.remove('add-to-bag-btn--loading');
            this.querySelector('.js-add-to-bag-error').classList.add('hidden');

            window.sendGTMData && window.sendGTMData({
              event: 'bundle_button_add_to_cart',
              variant_id: variant?.id,
              product_id: product.id,
              bundle_button_id: bundleButtonId,
              bundle_button_quantity: bundleButtonEl.dataset.bbQuantity,
              bundle_button_price_usd: bundleButtonEl.dataset.bbPrice,
              bundle_button_price_cad: bundleButtonEl.dataset.bbPriceCa,
              source: 'pdp'
            });

            window.dispatchEvent(new CustomEvent('update-cart'));
          } catch (err) {
            console.error('ERROR: ', err);
            bundleButtonEl.classList.remove('add-to-bag-btn--loading');
            bundleButtonEl.innerHTML = bundleButtonEl.dataset.addToBagText;
            let errorMsg;
            if (typeof err === 'string' && err.toLowerCase().includes('out of stock')) {
              errorMsg = 'Out of stock';
            } else if (typeof err === 'string' && err.toLowerCase().includes('cannot apply selling plan')) {
              errorMsg = bundleButtonEl.dataset.subscriptionError;
            } else if (this.hasBandCup()) {
              if (!this.state.bandSize && !this.state.cupSize) {
                errorMsg = bundleButtonEl.dataset.sizeError;
              } else if (this.state.bandSize && !this.state.cupSize) {
                errorMsg = bundleButtonEl.dataset.cupSizeError;
              } else if (!this.state.bandSize && this.state.cupSize) {
                errorMsg = bundleButtonEl.dataset.bandSizeError;
              }
            } else if (!this.hasBandCup()) {
              if (!this.dom.parentComponent.state.selectedSize) {
                errorMsg = bundleButtonEl.dataset.sizeError;
              }
            }

            if (!errorMsg) {
              errorMsg = bundleButtonEl.dataset.cartError;
            }
            this.querySelector('.js-add-to-bag-error').innerHTML = errorMsg;
            this.querySelector('.js-add-to-bag-error').classList.remove('hidden');
          }
        });
      }
    }
    handleBundleButton(color) {
      let bundleButtonEl = this.querySelector('.js-add-to-bag-bundle-button');

      if (bundleButtonEl) {
        const freshBtn = bundleButtonEl.cloneNode(true);
        bundleButtonEl.replaceWith(freshBtn);
        bundleButtonEl = freshBtn;
        const bbPdpEnabled = bundleButtonEl.dataset.bbPdpEnabled === 'true';
        const bbParamEnabled = bundleButtonEl.dataset.bbParamEnabled === 'true';
        const bbId = bundleButtonEl.dataset.bbId || '';
        const tags = this.dom.parentComponent.state.product.colors[color].tags;
        const tag = tags.find(tag => tag.includes('bundle-button-'));
        if (bbPdpEnabled && tag) {
          const quantityPriceText = tag.split('bundle-button-')[1];
          const quantity = +quantityPriceText.split('-')[0];
          const priceUsd = +quantityPriceText.split('-')[1] * 100;
          const priceCad = Math.round(priceUsd * window.i18n_cad_exchange_rate);
          let textContent;
          if (window.Shopify.country === 'CA') {
            textContent = `${quantity} for ${toMoney(priceCad)} ${window.Shopify.currency.active}`;
          } else {
            textContent = `${quantity} for ${toMoney(priceUsd)}`;
          }
          bundleButtonEl.dataset.bbQuantity = quantity;
          bundleButtonEl.dataset.bbPrice = priceUsd;
          bundleButtonEl.dataset.bbPriceCa = priceCad;
          if (bbParamEnabled && bbId) {
            const searchParams = new URLSearchParams(window.location.search);
            const bbCookie = jsCookie.get('_tl_bb_id');
            const bundleButtonParamEnabled = searchParams.get('bundleButton') || bbCookie;
            if (bundleButtonParamEnabled === bbId) {
              bundleButtonEl.textContent = textContent;
              bundleButtonEl.classList.remove('hidden');
              if (!bbCookie || (bbCookie && bbCookie !== bundleButtonParamEnabled)) window.setCookie('_tl_bb_id', bbId, { expires: 1 });
            } else {
              bundleButtonEl.classList.add('hidden');
              jsCookie.remove('_tl_bb_id');
            }
          } else {
            bundleButtonEl.textContent = textContent;
            bundleButtonEl.classList.remove('hidden');
          }
        } else {
          bundleButtonEl.classList.add('hidden');
        }

        bundleButtonEl.addEventListener('click', async (event) => {
          event.preventDefault();

          try {
            const variant = this.getVariant();

            if (!variant) {
              throw new Error('No variant selected');
            }

            bundleButtonEl.innerHTML = bundleButtonEl.dataset.loadingText;
            bundleButtonEl.classList.add('add-to-bag-btn--loading');

            const bundleButtonId = keygen('bundle-button');
            const product = this.getProduct().colors[this.dom.parentComponent.state.selectedColor];
            const payload = {
              id: variant.id,
              quantity: +bundleButtonEl.dataset.bbQuantity,
              properties: {
                _bbId: bundleButtonId,
                _bbQuantity: +bundleButtonEl.dataset.bbQuantity,
                _bbPrice: +bundleButtonEl.dataset.bbPrice,
                _bbPriceCa: +bundleButtonEl.dataset.bbPriceCa,
                _tags: filterProductTags(product.tags),
                _isBundleButtonDiscountKit: false,
                _hasDiscountKitApplied: false
              }
            };

            const price = variant.price;
            const compareAtPrice = variant.compare_at_price;
            if (window.Shopify.country === 'CA' && compareAtPrice && compareAtPrice > price) {
              payload.properties._compareAtPrice = Math.trunc((compareAtPrice / 100) / window.i18n_cad_exchange_rate) * 100;
              payload.properties._compareAtPriceCa = compareAtPrice;
            } else if (compareAtPrice && compareAtPrice > price) {
              payload.properties._compareAtPrice = compareAtPrice;
              payload.properties._compareAtPriceCa = Math.ceil((compareAtPrice / 100) * window.i18n_cad_exchange_rate) * 100;
            }

            await window.cartManager.addItem(payload);

            bundleButtonEl.innerHTML = bundleButtonEl.dataset.addToBagText;
            bundleButtonEl.classList.remove('add-to-bag-btn--loading');
            this.querySelector('.js-add-to-bag-error').classList.add('hidden');

            window.sendGTMData && window.sendGTMData({
              event: 'bundle_button_add_to_cart',
              variant_id: variant?.id,
              product_id: product.id,
              bundle_button_id: bundleButtonId,
              bundle_button_quantity: bundleButtonEl.dataset.bbQuantity,
              bundle_button_price_usd: bundleButtonEl.dataset.bbPrice,
              bundle_button_price_cad: bundleButtonEl.dataset.bbPriceCa,
              source: 'pdp'
            });

            window.dispatchEvent(new CustomEvent('update-cart'));
          } catch (err) {
            console.error('ERROR: ', err);
            bundleButtonEl.classList.remove('add-to-bag-btn--loading');
            bundleButtonEl.innerHTML = bundleButtonEl.dataset.addToBagText;
            let errorMsg;
            if (typeof err === 'string' && err.toLowerCase().includes('out of stock')) {
              errorMsg = 'Out of stock';
            } else if (typeof err === 'string' && err.toLowerCase().includes('cannot apply selling plan')) {
              errorMsg = bundleButtonEl.dataset.subscriptionError;
            } else if (this.hasBandCup()) {
              if (!this.state.bandSize && !this.state.cupSize) {
                errorMsg = bundleButtonEl.dataset.sizeError;
              } else if (this.state.bandSize && !this.state.cupSize) {
                errorMsg = bundleButtonEl.dataset.cupSizeError;
              } else if (!this.state.bandSize && this.state.cupSize) {
                errorMsg = bundleButtonEl.dataset.bandSizeError;
              }
            } else if (!this.hasBandCup()) {
              if (!this.dom.parentComponent.state.selectedSize) {
                errorMsg = bundleButtonEl.dataset.sizeError;
              }
            }

            if (!errorMsg) {
              errorMsg = bundleButtonEl.dataset.cartError;
            }
            this.querySelector('.js-add-to-bag-error').innerHTML = errorMsg;
            this.querySelector('.js-add-to-bag-error').classList.remove('hidden');
          }
        });
      }
    }
    afterRender() {
      if (document.querySelector('.js-variants-accordions-container')) {
        document.querySelector('.js-variants-accordions-container').classList.remove('hidden');
      }

      const variant = this.getVariant();
      const liquidForm = document.querySelector('#pdp-variants-form-liquid') || null;
      const acceleratedCheckoutButtons = liquidForm?.querySelector('.js-accelerated-checkout-buttons') || null;

      if(variant) {

        // Update Product Liquid Form
        if (liquidForm && acceleratedCheckoutButtons) {

          this.acceleratedCheckoutButtons = acceleratedCheckoutButtons;

          // Update all inputs with the variant ID
          liquidForm.querySelectorAll('input[name="id"]').forEach(input => {
            input.setAttribute('value', variant.id);
          });

          // Disable Accelerated Checkout Buttons
          //acceleratedCheckoutButtons?.classList.remove('hidden');
        }
      }
      else {
        // hide Accelerated Checkout Buttons
        //acceleratedCheckoutButtons?.classList.add('hidden');
      }

      if (this.isOutOfStock()) {
        // Hide Accelerated Checkout Buttons
        //this.acceleratedCheckoutButtons?.classList.add('hidden');
      }

      this.querySelectorAll('.js-colors .js-input').forEach(color => {
        const category = color.closest('.color-group').getAttribute('data-color-group');
        color.addEventListener('change', () => {
          if (this.querySelector('.js-active-color')) this.querySelector('.js-active-color').textContent = color.value.replace(/-/g, ' ');
          this.handleChangeUrl(color.value);

          setTimeout(() => {
            const detail = {
              selectedColor: color.value,
            }
            if (category) {
              detail.colorCategory = {
                [category]: {
                  title: category,
                  color: color.value,
                },
              }
            }
            this.dispatchEvent(new CustomEvent('set-state', {
              bubbles: true,
              detail: detail
            }));
            this.sendColorChangeGTMData(color.value);
            this.handleBundleButton(color.value);
            this.handleBundleButtonDiscountKit(color.value);
            this.updateLoyaltyBanner();
          }, 0);
        });
      });

      /*
      if (!this.state.priveWidgetLoaded) {
        document.dispatchEvent(new Event(window.PRIVE_WIDGET_SETTINGS.initEventListener));
        this.state.priveWidgetLoaded = true;
        this.state.currentProductId = document.querySelector('#prive-widget-injection-point').getAttribute('data-product-id');
      }
      */
      this.handleSizeChangeNewDesign();
      this.scrollToCheckedSize();
      this.handleAddToBag();
      this.handleColorCategoryChange();
    }
    scrollToCheckedSize() {
      if (!this.state.bandSize && !this.state.cupSize) return;

      const container = document.querySelector('.container-js-sizes-element');
      if (!container) return;

      const checkedInput = container.querySelector('.js-size-input:checked');
      if (!checkedInput) return;

      const label = checkedInput.closest('.js-sizes-label');
      if (!label) return;

      label.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest'
      });
    }
    handleChangeUrl(color) {
      if (this.props.replaceUrl && this.dom.parentComponent.state.product.colors[color].parent_collection_type !== 'additional-styles') {
        history.replaceState('', '', this.dom.parentComponent.state.product.colors[color].handle);
        //document.querySelector('#prive-widget-injection-point')?.setAttribute('data-product-id', this.dom.parentComponent.state.product.colors[color].id);
        this.state.currentProductId = this.dom.parentComponent.state.product.colors[color].id;
        //document.querySelector('#prive-widget-injection-point > input[name="id"]')?.setAttribute('value', Object.values(this.dom.parentComponent.state.product.colors[color].variants)[0].id);
        //if (this.state.priveWidgetLoaded) {
          // window.PRIVE_WIDGET.PriveWidget.createWidget();
          // TODO: Remover comment above and uncomment below when Prive fixes issue with createWidget
        //}
      }
    }
    handleColorCategoryChange() {
      const activeCategory = this.dom.parentComponent.state?.colorCategory?.activeCategory;
      if (!activeCategory) return;
      const elements = this.querySelectorAll('.js-new-colors') || [];
      const elementsGroup = this.querySelectorAll('.js-colors .color-group') || [];
      elements.forEach(el => el?.classList.remove('active-category'));
      elementsGroup.forEach( el => {
        el.classList.remove('hidden');
        el.classList.remove('active-category');

        if (el.dataset.colorGroup === activeCategory)
          el.classList.add('active-category');
        else
          el.classList.add('hidden');
      });
      this.querySelector(`.js-new-colors.js-${activeCategory}-colors-news`)?.classList.add('active-category');
    }
    handleSizeChangeNewDesign() {
      this.querySelectorAll('.js-size-input').forEach(input => {
        input.addEventListener('change', ({ target }) => {
          let size;
          if (input?.name === 'band') this.state.bandSize = input.value;
          if (input?.name === 'cup') this.state.cupSize = input.value;

          if (input?.name === 'band' && target.parentElement?.classList?.contains('disabled')) {
            this.state.disabledBandSize = true;
          } else {
            this.state.disabledBandSize = false;
          }

          if (this.querySelector(`.js-sizes--band`)){
            if (this.state.bandSize && this.state.cupSize) size = `${this.state.bandSize}${this.state.cupSize}`;
          } else {
            size = input.value;
          }

          if (this.props.isPreciseSizing) {
            const mappedSize = getMappedFormfitSizes(input.value);
            let _size = '';
            mappedSize.forEach((e, i) => {
              if (i === mappedSize.length - 1) return _size += `and ${e} `;
              if (i === mappedSize.length - 2) return _size += `${e} `;
              _size += `${e}, `;
            });

            this.state.formFitSizeMessage = `${size} fits ${_size}`;
          }

          const addToBagBtn = this.querySelector('.js-add-to-bag');
          if (addToBagBtn) {
            addToBagBtn.innerHTML = size
              ? addToBagBtn.dataset.addToBagText
              : (addToBagBtn.dataset.selectSizeText || 'Select Size');
          }

          const detail = {
            componentsToRender: 'PDPProductVariants, PDPProductInfo',
            selectedSize: size || ''
          };
          setTimeout(() => {
            this.dispatchEvent(new CustomEvent('set-state', { bubbles: true, detail }));
            if (size) this.sendSizeChangeGTMData(size);
          }, 0);
          /*
          if (!(this.dom.parentComponent.state.selectedSize && this.getSiblingColor().availableSizes.indexOf(this.dom.parentComponent.state.selectedSize) === -1) && this.getVariant() && window.PRIVE_WIDGET?.PriveWidget.products[this.state.currentProductId]?.selling_plan_groups) {
            const formPriveWidgetInjection = document.querySelector('#prive-widget-injection-point');
            formPriveWidgetInjection.setAttribute('data-product-id', this.state.currentProductId);
            document.querySelector('#prive-widget-injection-point > input[name="id"]').setAttribute('value', this.getVariant().id);
            window.PRIVE_WIDGET.PriveWidget.buildWidget(
              window.PRIVE_WIDGET.PriveWidget.products[this.state.currentProductId],
              formPriveWidgetInjection,
              this.getVariant().id
            );
            window.PRIVE_WIDGET.PriveWidget._injectSellingPlanInput();
            window.PRIVE_WIDGET.PriveWidget._variantSetupWidget(
              200,
              document.querySelector('.prive-widget'),
              window.PRIVE_WIDGET.PriveWidget.products[this.state.currentProductId],
              this.getVariant().id
            );
          }
          */
        });
      });
      this.updateLoyaltyBanner();
    }
    handleSizeChange() {
      this.querySelectorAll('.js-size').forEach(group => {
        const dropdown = group.querySelector('.js-size-dropdown');
        dropdown.addEventListener('change', () => {
          const sizeOption = dropdown.options[dropdown.selectedIndex];
          if (sizeOption.dataset.name === 'band') this.state.bandSize = sizeOption.dataset.size;
          if (sizeOption.dataset.name === 'cup') this.state.cupSize = sizeOption.dataset.size;
          let size;
          if (this.querySelector(`.js-size-band`)) {
            if (this.state.bandSize && this.state.cupSize) size = `${this.state.bandSize}${this.state.cupSize}`;
          } else {
            size = sizeOption.dataset.size;
          }

          // Update selected size state and rerender components
          const detail = {
            componentsToRender: 'PDPProductVariants, PDPProductInfo'
          }
          if (size) detail.selectedSize = size;
          this.dispatchEvent(new CustomEvent('set-state', {
            bubbles: true,
            detail: detail
          }));
          if (size) {
            this.sendSizeChangeGTMData(size);
          }

          /*
          if (!(this.dom.parentComponent.state.selectedSize && this.getSiblingColor().availableSizes.indexOf(this.dom.parentComponent.state.selectedSize) === -1) && this.getVariant() && window.PRIVE_WIDGET?.PriveWidget.products[this.state.currentProductId]?.selling_plan_groups) {
            const formPriveWidgetInjection = document.querySelector('#prive-widget-injection-point');
            formPriveWidgetInjection.setAttribute('data-product-id', this.state.currentProductId);
            document.querySelector('#prive-widget-injection-point > input[name="id"]').setAttribute('value', this.getVariant().id);
            window.PRIVE_WIDGET.PriveWidget.buildWidget(
              window.PRIVE_WIDGET.PriveWidget.products[this.state.currentProductId],
              formPriveWidgetInjection,
              this.getVariant().id
            );
            window.PRIVE_WIDGET.PriveWidget._injectSellingPlanInput();
            window.PRIVE_WIDGET.PriveWidget._variantSetupWidget(
              200,
              document.querySelector('.prive-widget'),
              window.PRIVE_WIDGET.PriveWidget.products[this.state.currentProductId],
              this.getVariant().id
            );
          }
          */
        });
      });
      this.updateLoyaltyBanner();
    }
    async handleAddToBag() {
      const addToBagBtn = this.querySelector('.js-add-to-bag');
      const errorEl = this.querySelector('.js-add-to-bag-error');

      addToBagBtn.addEventListener('click', async (event) => {
        event.preventDefault();

        const waitlistActive = addToBagBtn.dataset.waitlistActive === 'true';
        if ((waitlistActive || this.isOutOfStock()) && this.canShowBackInStock()) {
          this.openBackInStockModal();
          return;
        }

        try {
          // Prevent multiple clicks
          if (addToBagBtn.classList.contains('add-to-bag-btn--loading')) {
            return;
          }

          const variant = this.getVariant();

          if (!variant) {
            throw new Error('No variant selected');
          }

          addToBagBtn.innerHTML = addToBagBtn.dataset.loadingText;
          addToBagBtn.classList.add('add-to-bag-btn--loading');

          const payload = {
            id: variant.id,
            quantity: 1,
            properties: {
              _tags: filterProductTags(this.getProduct().colors[this.dom.parentComponent.state.selectedColor]?.tags)
            }
          };

          const sellingPlanInput = this.querySelector('input[name="selling_plan"]');
          if (sellingPlanInput && sellingPlanInput.value) {
            payload.selling_plan = Number(sellingPlanInput.value);
          }

          const price = variant.price;
          const compareAtPrice = variant.compare_at_price;
          if (window.Shopify.country === 'CA' && compareAtPrice && compareAtPrice > price) {
            payload.properties._compareAtPrice = Math.trunc((compareAtPrice / 100) / window.i18n_cad_exchange_rate) * 100;
            payload.properties._compareAtPriceCa = compareAtPrice;
          } else if (compareAtPrice && compareAtPrice > price) {
            payload.properties._compareAtPrice = compareAtPrice;
            payload.properties._compareAtPriceCa = Math.ceil((compareAtPrice / 100) * window.i18n_cad_exchange_rate) * 100;
          }

          if (window.cart_settings?.checkoutFinalSaleLabel && payload.properties._tags?.some(tag => tag.toLowerCase().replaceAll(/\s|-|_/g, '').includes('finalsale'))) {
            payload.properties['Final Sale'] = window.cart_settings.checkoutFinalSaleLabel;
          }

          await window.cartManager.addItem(payload);

          addToBagBtn.innerHTML = addToBagBtn.dataset.addToBagText;
          addToBagBtn.classList.remove('add-to-bag-btn--loading');
          errorEl.classList.add('hidden');

          window.dispatchEvent(new CustomEvent('update-cart'));
        } catch (err) {
          console.error('ERROR: ', err);
          addToBagBtn.classList.remove('add-to-bag-btn--loading');
          const hasSize = !!this.dom.parentComponent.state.selectedSize;
          addToBagBtn.innerHTML = hasSize
            ? addToBagBtn.dataset.addToBagText
            : (addToBagBtn.dataset.selectSizeText || 'Select Size');
          let errorMsg;
          if (typeof err === 'string' && err.toLowerCase().includes('out of stock')) {
            errorMsg = 'Out of stock';
          } else if (typeof err === 'string' && err.toLowerCase().includes('cannot apply selling plan')) {
            errorMsg = addToBagBtn.dataset.subscriptionError;
          } else if (this.hasBandCup()) {
            if (!this.state.bandSize && !this.state.cupSize) {
              errorMsg = addToBagBtn.dataset.sizeError;
            } else if (this.state.bandSize && !this.state.cupSize) {
              errorMsg = addToBagBtn.dataset.cupSizeError;
            } else if (!this.state.bandSize && this.state.cupSize) {
              errorMsg = addToBagBtn.dataset.bandSizeError;
            }
          } else if (!this.hasBandCup()) {
            if (!this.dom.parentComponent.state.selectedSize) {
              errorMsg = addToBagBtn.dataset.sizeError;
            }
          }

          if (!errorMsg) {
            errorMsg = addToBagBtn.dataset.cartError;
          }
          errorEl.innerHTML = errorMsg;
          errorEl.classList.remove('hidden');
        }
      });
      this.handleBundleButton(this.dom.parentComponent.state.selectedColor);
      this.handleBundleButtonDiscountKit(this.dom.parentComponent.state.selectedColor);
    }
    canShowBackInStock() {
      if (!this.getVariant()) return false;
      if (this.getSiblingColor().tags.includes('bis-hidden') || this.getSiblingColor().tags.includes('bis-paused')) return false;
      return !!this.querySelector('.js-template-back-in-stock');
    }
    openBackInStockModal() {
      if (!this.canShowBackInStock()) return;
      if (document.querySelector('.js-back-in-stock-modal')) return;

      const modalEl = document.createElement('div');
      modalEl.className = 'modal is-open js-back-in-stock-modal component-pdp-product';
      modalEl.setAttribute('aria-hidden', 'false');

      const overlayEl = document.createElement('div');
      overlayEl.className = 'modal__overlay js-back-in-stock-modal-overlay';

      const containerEl = document.createElement('div');
      containerEl.className = 'modal__container modal__container--back-in-stock js-modal-container';
      containerEl.setAttribute('role', 'dialog');
      containerEl.setAttribute('aria-modal', 'true');

      const closeBtnEl = document.createElement('button');
      closeBtnEl.type = 'button';
      closeBtnEl.className = 'modal__close js-back-in-stock-modal-close';
      closeBtnEl.setAttribute('aria-label', 'Close');

      const contentEl = document.createElement('div');
      contentEl.className = 'modal__content js-back-in-stock-modal-content';

      const bisContainerEl = document.createElement('div');
      bisContainerEl.className = 'js-back-in-stock';

      contentEl.appendChild(bisContainerEl);
      containerEl.appendChild(closeBtnEl);
      containerEl.appendChild(contentEl);
      overlayEl.appendChild(containerEl);
      modalEl.appendChild(overlayEl);

      const backInStockTemplate = this.querySelector('.js-template-back-in-stock').content.cloneNode(true);
      backInStockTemplate.querySelector('component-pdp-product-back-in-stock').dataset.productId = this.getSiblingColor().id;
      backInStockTemplate.querySelector('component-pdp-product-back-in-stock').dataset.variantId = this.getVariant().id;
      bisContainerEl.appendChild(backInStockTemplate);

      const closeModal = () => {
        modalEl.remove();
        document.body.classList.remove('overflow-hidden');
      };

      closeBtnEl.addEventListener('click', closeModal);
      overlayEl.addEventListener('click', event => {
        if (event.target.classList.contains('js-back-in-stock-modal-overlay')) {
          closeModal();
        }
      });

      document.body.appendChild(modalEl);
      document.body.classList.add('overflow-hidden');
    }
    isOutOfStock() {
      return this.dom.parentComponent.state.selectedSize && this.getSiblingColor() && this.getSiblingColor().availableSizes.indexOf(this.dom.parentComponent.state.selectedSize) === -1;
    }
    sendColorChangeGTMData(color) {
      window.sendGTMData && window.sendGTMData({
        event: 'pdp_item_color_select',
        product_id: this.getSiblingColor().id,
        color_selected: color,
        source: 'pdp'
      });
    }
    sendSizeChangeGTMData(size) {
      window.sendGTMData && window.sendGTMData({
        event: 'pdp_item_size_select',
        product_id: this.getSiblingColor().id,
        size_selected: size,
        source: 'pdp'
      });
    }
  });
}

// Product details accordion subcomponent
if (!customElements.get('component-pdp-product-accordion')) {
  customElements.define('component-pdp-product-accordion', class PDPProductAccordion extends PDPProductSubComponent {
    constructor() {
      super();
      this.state = {
        expanded: (window.innerWidth <= 1024 ? this.dataset.expandedMob : this.dataset.expanded) === "true"
      }
      this.props.type = this.dataset.type;
      this.props.showProductDetailsIcons = this.dataset.showProductDetailsIcons === 'true';
    }
    render() {
      const template = this.template.content.cloneNode(true);
      const icon = document.createElement("span");
      icon.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M7.21967 9.21967C7.51256 8.92678 7.98744 8.92678 8.28033 9.21967L11.75 12.6893L15.2197 9.21967C15.5126 8.92678 15.9874 8.92678 16.2803 9.21967C16.5732 9.51256 16.5732 9.98744 16.2803 10.2803L12.2803 14.2803C12.1397 14.421 11.9489 14.5 11.75 14.5C11.5511 14.5 11.3603 14.421 11.2197 14.2803L7.21967 10.2803C6.92678 9.98744 6.92678 9.51256 7.21967 9.21967Z" fill="#2C272D"></path></svg>';
      if (template.querySelector('.js-accordion-icon')) template.querySelector('.js-accordion-icon').appendChild(icon);
      let accordionContent;
      if (this.props.type === "product_details") {
        // Render product detail icons
        if (this.props.showProductDetailsIcons && this.getSiblingColor().product_details_icons) {
          template.querySelectorAll('.js-product-details-icons > *:not(template)').forEach(item => item.remove());
          for (let icon of this.getSiblingColor().product_details_icons) {
            if (icon.image && icon.text) {
              const iconTemplate = template.querySelector('.js-template-product-detail-icon').content.cloneNode(true);
              iconTemplate.querySelector('.js-product-detail-img').dataset.src = icon.image.src;
              iconTemplate.querySelector('.js-product-detail-img').dataset.sizes = 'auto';
              iconTemplate.querySelector('.js-product-detail-img').width = icon.image.width;
              iconTemplate.querySelector('.js-product-detail-img').height = icon.image.height;
              iconTemplate.querySelector('.js-product-detail-img').classList.add('lazyload');
              iconTemplate.querySelector('.js-product-details-icon-text').innerHTML = icon.text;
              template.querySelector('.js-product-details-icons').appendChild(iconTemplate);
            }
          }
        }
        if (this.getSiblingColor().accordions.product_details) {
          accordionContent = this.getSiblingColor().accordions.product_details;
        } else {
          accordionContent = this.getSiblingColor().description;
        }
      } else if (this.props.type === "size_fit") {
        accordionContent = this.getSiblingColor().accordions.size_fit;
      } else if (this.props.type === "fabric_care") {
        accordionContent = this.getSiblingColor().accordions.fabric_care;
      } else {
        accordionContent = template.querySelector('.js-accordion-content') ? template.querySelector('.js-accordion-content').innerHTML : null;
      }
      if (accordionContent) {
        template.querySelector('.js-accordion-content').innerHTML = accordionContent;
        this.append(template);
        this.afterRender();
      }
      if (this.state.expanded && this.querySelector('.js-accordion-details')) {
        this.querySelector('.js-accordion-details').open = true;
      }
    }
    afterRender() {
      this.querySelector('.js-accordion-details').addEventListener('click', () => {
        this.state.expanded = !this.state.expanded;
        this.sendAccordionGTMData();
      });
      if (this.props.type === "reviews") {
        document.addEventListener('scroll-to-reviews', () => {
          this.state.expanded = true;
          this.querySelector('.js-accordion-details').open = true;
          this.scrollIntoView({ behavior: "auto", block: "start", inline: "nearest" });
        });
      }
    }
    sendAccordionGTMData() {
      window.sendGTMData && window.sendGTMData({
        event: `pdp_accordion_${this.props.type}`,
        product_id: this.getSiblingColor().id,
        expanded: this.state.expanded,
        source: 'pdp'
      });
    }
  });
}

// Back in stock component
if (!customElements.get('component-pdp-product-back-in-stock')) {
  customElements.define('component-pdp-product-back-in-stock', class PDPProductBackInStock extends HTMLElement {
    constructor() {
      super();
      this.state = {
        acceptsMarketing: this.dataset.bisKlaviyoEnabled === 'true',
        email: ''
      }
      this.props = {
        variantId: this.dataset.variantId,
        productId: this.dataset.productId,
        disServiceKey: JSON.parse(this.dataset.pskey).disServiceKey,
        cryptoSecret: this.dataset.cryptoSecret,
        bisKlaviyoEnabled: this.dataset.bisKlaviyoEnabled === 'true',
        bisKlaviyoPublicKey: this.dataset.bisKlaviyoPublicKey || '',
        bisKlaviyoRevision: this.dataset.bisKlaviyoRevision || '2024-10-15',
        bisKlaviyoListId: this.dataset.bisKlaviyoListId || ''
      }
      this.render();
    }
    render() {
      const template = this.querySelector('template').content.cloneNode(true);
      this.append(template);
      this.afterRender();
    }
    afterRender() {
      this.querySelector('.js-email-input').addEventListener('input', input => {
        this.state.email = input.srcElement?.value;
      });
      if (!this.props.bisKlaviyoEnabled) {
        this.querySelector('.js-subscribe-checkbox')?.addEventListener('change', checkbox => {
          this.state.acceptsMarketing = checkbox.srcElement?.checked;
        });
      }
      this.querySelector('.js-form').addEventListener('submit', async (event) => {
        event.preventDefault();

        // Identify user on Klaviyo
        window.klaviyoIdentify && window.klaviyoIdentify(this.state.email);

        if (this.state.acceptsMarketing) {
          await this.registerProspect();
        }
        if (this.props.bisKlaviyoEnabled) {
          if (this.props.bisKlaviyoListId) {
            await this.registerToNewsletterInKlaviyo(this.state.email, this.props.bisKlaviyoListId);
          }
          await this.registerBackInStockInKlaviyo(this.state.email);
        } else {
          await this.registerBackInStock();
        }
      });
    }
    async sendEmailSubscribeGTMEvent() {
      await import(document.querySelector('.js-component-pdp-product').dataset.browserCrypto);

      const cipherInstance = browserCrypto.createCipher(
        'aes-256-cbc',
        this.props.cryptoSecret,
      );
      const encryptedEmail = cipherInstance.update(this.state.email, 'utf8', 'hex');
      const finalEmailHash = encryptedEmail + cipherInstance.final('hex');

      window.sendGTMData && window.sendGTMData({
        event: 'email_subscribe',
        email: finalEmailHash,
        source: 'pdp_capture_back_in_stock'
      });
    }
    async registerProspect() {
      try {
        const data = {
          email: this.state.email,
          source: 'pdp_capture',
          user_locale: window.userLocale
        };
        const url = window.location.hostname.includes('trythirdlove.com') ? 'https://dis-apis.trythirdlove.com/v1/prospects/registration' : 'https://dis-apis.thirdlove.com/v1/prospects/registration';
        let response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': this.props.disServiceKey,
          },
          method: 'POST',
          body: JSON.stringify(data)
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } catch (errDis) {
        console.error('Error while sending new prospect to DIS', errDis);
      }
    }
    async registerToNewsletterInKlaviyo(email, listId) {
      try {
        const payload = {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'PDP',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email
                  }
                }
              }
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: listId
                }
              }
            }
          }
        };

        const options = {
          method: 'POST',
          headers: {revision: this.props.bisKlaviyoRevision, 'content-type': 'application/vnd.api+json'},
          body: JSON.stringify(payload)
        };

        const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${this.props.bisKlaviyoPublicKey}`, options);
        if (response.ok) return;
        const jsonResponse = await response.json();
        throw new Error(jsonResponse);
      } catch (err) {
        console.error('Error subscribing user to the newsletter: ', JSON.stringify(err));
      }
    }
    async registerBackInStock() {
      let response;
      try {
        response = await BIS.create(this.state.email, this.props.variantId, this.props.productId, {
          accepts_marketing: this.state.acceptsMarketing,
          quantity_required: 1,
          customer_utc_offset: new Date().getTimezoneOffset()
        });
        if (response.status !== 'OK' && !response.errors?.base[0]?.includes('already registered')) {
          throw new Error(response);
        } else {
          if (this.state.acceptsMarketing) {
            await this.sendEmailSubscribeGTMEvent();
          }
          this.querySelector('.js-content').classList.add('hidden');
          this.querySelector('.js-error-msg').classList.add('hidden');
          this.querySelector('.js-success-msg').classList.remove('hidden');
        }
      } catch (errBis) {
        console.error('Error while sending information to BIS App', errBis?.errors?.base);
        this.querySelector('.js-error-msg').classList.remove('hidden');
        this.querySelector('.js-success-msg').classList.add('hidden');
      }
    }
    async registerBackInStockInKlaviyo(email) {
      try {
        const response = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${this.props.bisKlaviyoPublicKey}`, {
          method: "POST",
          headers: {
            'Content-Type': 'application/vnd.api+json',
            'revision': this.props.bisKlaviyoRevision
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email
                    }
                  }
                }
              },
              relationships: {
                variant: {
                  data: {
                    type: "catalog-variant",
                    id: `$shopify:::$default:::${this.props.variantId}`
                  }
                }
              }
            }
          })
        });
        if (!response.ok) {
          const jsonResponse = await response.json();
          throw new Error(jsonResponse);
        } else {
          this.querySelector('.js-content').classList.add('hidden');
          this.querySelector('.js-error-msg').classList.add('hidden');
          this.querySelector('.js-success-msg').classList.remove('hidden');
        }
      } catch (err) {
        console.error('Back in stock subscription error: ', JSON.stringify(err));
        this.querySelector('.js-error-msg').classList.remove('hidden');
        this.querySelector('.js-success-msg').classList.add('hidden');
      }
    }
  });
}
// Fit Form Calculator subcomponent (nested within 'component-pdp-product-variants')
if (!customElements.get('component-fitform-calculator')) {
  customElements.define('component-fitform-calculator', class ProductFitformCalculator extends PDPProductSubComponent {
    constructor() {
      super();
      // Nested components should use state on parent subcomponent level
      this.state = document.querySelector('component-pdp-product-variants').state;
    }
    async connectedCallback() {
      if (this.dom.parentComponent.props.useTrueSize) {
        this.initSizeFromTrueSize();
      }
      this.render();
    }
    initSizeFromTrueSize() {
      if (this.dom.parentComponent.state.selectedSize) {
        this.state.formFitSize = this.dom.parentComponent.state.selectedSize;
      }
    }
    render() {
      const template = this.template.content.cloneNode(true);
      this.renderFormFitCalculator(template);
      this.append(template);
      this.afterRender();
    }
    renderFormFitCalculator(template) {
      const formFitCalculatorTemplate = template.querySelector('.js-template-form-fit-calculator').content.cloneNode(true);
      const bandSizeDropdown = formFitCalculatorTemplate.querySelector('.js-band-sizes');
      // Render band dropdown
      const bandSizes = Object.keys(getFormfitCarriedSizes());
      for (const bandSize of bandSizes) {
        const option = document.createElement('option');
        option.value = bandSize;
        option.innerHTML = bandSize;
        if (bandSize === this.state.formFitBandSize) {
          option.selected = true;
          this.state.formFitBandSize = bandSize;
        }
        bandSizeDropdown.appendChild(option);
      }
      const formFitBandSize = this.state.formFitBandSize;
      if (formFitBandSize) {
        bandSizeDropdown.options[0].textContent = `Band: ${formFitBandSize}`;
        bandSizeDropdown.options[0].value = formFitBandSize;
        bandSizeDropdown.selectedIndex = 0;
      }
      let cupSizes = Object.values(getFormfitCarriedSizes()).flat(1);
      if (this.state.formFitBandSize) {
        cupSizes = getFormfitCarriedSizes()[this.state.formFitBandSize];
      }
      const fitformSizes = Object.keys(this.getSiblingColor().variants).flat(1);
      this.renderFormFitCupSizes(formFitCalculatorTemplate, cupSizes);
      this.renderFormfitSizes(formFitCalculatorTemplate, fitformSizes);
      template.querySelector('.js-container').appendChild(formFitCalculatorTemplate);
    }
    renderFormFitCupSizes(template, cupSizes) {
      const cupSizeDropdown = template.querySelector('.js-cup-sizes');
      cupSizeDropdown.innerHTML = '';
      // Get unique values
      cupSizes = [...new Set(cupSizes)];
      cupSizes.sort();
      cupSizes.sort(a => {
        if (a === 'AA') {
          return -1;
        }
        return 0;
      });
      const defaultOption = document.createElement('option');
      defaultOption.value = "Cup";
      defaultOption.innerHTML = "Cup";
      cupSizeDropdown.appendChild(defaultOption);
      for (const cupSize of cupSizes) {
        const option = document.createElement('option');
        let label = cupSize;
        if (cupSize === 'E') label += ' (DD)';
        if (cupSize === 'E½') label += ' (DD½)';
        if (cupSize === 'E-E½') label += ' (DD-DD½)';
        if (cupSize === 'F') label += ' (DDD)';
        if (cupSize === 'G') label += ' (DDDD)';
        if (cupSize === 'H') label += ' (DDDDD)';
        if (cupSize === 'I') label += ' (DDDDDD)';
        option.value = cupSize;
        option.innerHTML = label;
        if (cupSize === this.state.formFitCupSize) {
          option.selected = true;
        }
        cupSizeDropdown.appendChild(option);
      }
        const formFitCupSize = this.state.formFitCupSize;
        if (!formFitCupSize) return;
        cupSizeDropdown.options[0].textContent = `Cup: ${formFitCupSize}`;
        cupSizeDropdown.options[0].value = formFitCupSize;
        cupSizeDropdown.selectedIndex = 0;
    }
    renderFormfitSizes(template, formfitSizes) {
      const formfitSizeDropdown = template.querySelector('.js-formfit-sizes');
      formfitSizeDropdown.innerHTML = '';
      // Get unique values
      formfitSizes = [...new Set(formfitSizes)];
      const defaultOption = document.createElement('option');
      defaultOption.value = "Thirdlove Size";
      defaultOption.innerHTML = "Precise Size™";
      formfitSizeDropdown.appendChild(defaultOption);
      for (const formfitSize of formfitSizes) {
        const option = document.createElement('option');
        option.value = formfitSize;
        option.innerHTML = formfitSize;
        if (formfitSize === this.state.formFitSize) {
          option.selected = true;
        }
        formfitSizeDropdown.appendChild(option);
      }
    }
    afterRender() {
      this.handleFormFitCalculator();
    }
    handleFormFitCalculator() {
      this.querySelector('.js-band-sizes').addEventListener('change', ( { target }) => {
        const carriedCups = getFormfitCarriedSizes()[target.value];
        this.state.formFitBandSize = target.value;
        this.querySelector('.js-cup-sizes').getElementsByTagName('option')[0].selected = 'selected';
        this.querySelector('.js-formfit-sizes').getElementsByTagName('option')[0].selected = 'selected';
        this.state.formFitCupSize = '';
        this.state.formFitSize = '';
        const selectedValue = target.value;
        const label = selectedValue ? `Band: ${selectedValue}` : "Band";
        target.options[0].textContent = label;
        target.selectedIndex = 0;
        this.renderFormFitCupSizes(this, carriedCups);
        this.calculate(false);
      });

      this.querySelector('.js-cup-sizes').addEventListener('change', ({ target }) => {
        this.state.formFitCupSize = target.value;
        this.calculate(false);
      });
      this.querySelector('.js-formfit-sizes').addEventListener('change', ({ target }) => {
        this.state.formFitSize = target.value;
        this.calculate(true);
      });
      this.renderSizeMessage();
    }
    renderSizeMessage() {
      const formfitSizeDropdown = this.querySelector('.form-fit-calculator');
      const message = formfitSizeDropdown.querySelector('.message');
      const mappedSize = getMappedFormfitSize(`${this.state.formFitBandSize}${this.state.formFitCupSize}`);
      if (!mappedSize) return;
      message.classList.remove('hidden');
      message.querySelector('strong').innerText = mappedSize;
    }
    calculate(clearOptions) {
      let mappedSize;
      if (!clearOptions) {
        if (this.state.formFitBandSize && this.state.formFitCupSize) {
          mappedSize = getMappedFormfitSize(`${this.state.formFitBandSize}${this.state.formFitCupSize}`);
          if (mappedSize) {
            this.state.formFitSize = mappedSize;
          }
        }
      } else {
        mappedSize = this.state.formFitSize;
        this.querySelector('.js-band-sizes').getElementsByTagName('option')[0].selected = 'selected';
        this.querySelector('.js-cup-sizes').getElementsByTagName('option')[0].selected = 'selected';
        this.state.formFitBandSize = '';
        this.state.formFitCupSize = '';
      }
      if (mappedSize) {
        this.updateSize(mappedSize);
        /*
        if (!(this.getSiblingColor().availableSizes.indexOf(mappedSize) === -1)) {
          const formPriveWidgetInjection = document.querySelector('#prive-widget-injection-point');
          formPriveWidgetInjection.setAttribute('data-product-id', this.state.currentProductId);
          document.querySelector('#prive-widget-injection-point > input[name="id"]').setAttribute('value', this.getVariant().id);
          window.PRIVE_WIDGET.PriveWidget.buildWidget(
            window.PRIVE_WIDGET.PriveWidget.products[this.state.currentProductId],
            formPriveWidgetInjection,
            this.getVariant().id
          );
          window.PRIVE_WIDGET.PriveWidget._injectSellingPlanInput();
          window.PRIVE_WIDGET.PriveWidget._variantSetupWidget(
            200,
            document.querySelector('.prive-widget'),
            window.PRIVE_WIDGET.PriveWidget.products[this.state.currentProductId],
            this.getVariant().id
          );
        }
        */
      }
    }
    updateSize(size) {
      // Update selected size state and rerender components
      const detail = {
        componentsToRender: 'PDPProductVariants'
      }
      detail.selectedSize = size;
      this.dispatchEvent(new CustomEvent('set-state', {
        bubbles: true,
        detail: detail
      }));
    }
  })
}

if (!customElements.get('component-pdp-videos')) {
  customElements.define('component-pdp-videos', class ProductVideos extends HTMLElement {
    constructor() {
      super();
      this.state = {
        selectedVideoId: null
      }
      this.props = {
        productUrl: this.dataset.productUrl
      }
    }
    connectedCallback() {
      this.checkSafari();
      if (window.location.href.includes('videoId')) {
        const urlParams = new URLSearchParams(window.location.search);
        const videoId = urlParams.get('videoId');
        if (videoId) {
          this.state.selectedVideoId = videoId;
          this.render(true);
          this.afterRender();
        }
      }
      this.querySelectorAll('.js-pdp-video').forEach(video => {
        video.addEventListener('click', () => {
          this.state.selectedVideoId = video.dataset.mainVideoId;
          this.render();
          this.afterRender();
        });
      });
      this.handleScroll();
    }
    handleScroll() {
      const container = this.querySelector('.js-pdp-videos');
      const next = this.querySelector('.js-videos-next');
      const prev = this.querySelector('.js-videos-prev');
      if (container && next && prev) {
        if (container.scrollWidth > container.clientWidth) {
          next.classList.remove('disabled');
        }
        container.addEventListener('scroll', () => {
          if (container.scrollLeft > 0) {
            prev.classList.remove('disabled');
          } else {
            prev.classList.add('disabled');
          }
          // Hide next button if scrolled to end of slider
          if (container.scrollLeft < (container.scrollWidth - container.offsetWidth - 10)) {
            next.classList.remove('disabled');
          } else {
            next.classList.add('disabled');
          }
        }, { passive: true });
        prev.addEventListener('click', () => {
          container.scrollBy({ top: 0, left: -325, behavior: 'smooth' });
        });
        next.addEventListener('click', () => {
          container.scrollBy({ top: 0, left: 325, behavior: 'smooth' });
        });
      }
    }
    checkSafari() {
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (isSafari) {
        this.classList.add("safari");
      }
    }
    render(muted = false) {
      if (this.state.selectedVideoId) {
        const videoTemplate = this.querySelector('.js-template-main-video').content.cloneNode(true);
        const video = this.querySelector(`.js-pdp-video[data-main-video-id="${this.state.selectedVideoId}"]`);
        if (video && video.dataset.mainVideoSrc) {
          videoTemplate.querySelector('.js-main-video-source').src = video.dataset.mainVideoSrc;
          videoTemplate.querySelector('.js-main-video-source').type = video.dataset.mainVideoType;
          if (muted) {
            videoTemplate.querySelector('.js-main-video').muted = true;
          }
          this.querySelector('.js-main-video-container').innerHTML = '';
          this.querySelector('.js-main-video-container').append(videoTemplate);
        }
      }
    }
    afterRender() {
      if (this.querySelector('.js-main-video')) {
        this.querySelector('.js-main-video').load();
      }
      this.querySelector('.js-main-video-close').addEventListener('click', () => {
        this.querySelector('.js-main-video-container').innerHTML = '';
      });
      // Copy link to PDP with video id to clipboard
      this.querySelector('.js-share-video').addEventListener('click', () => {
        navigator.clipboard.writeText(`${this.props.productUrl}?videoId=${this.state.selectedVideoId}`);
        this.querySelector('.js-share-video-confirmation').classList.remove('hidden');
        setTimeout(() => {
          this.querySelector('.js-share-video-confirmation').classList.add('hidden');
        }, 2000);
      });
    }
  });
}

var _recurlyWidgetEl = null;

if (!customElements.get('data-recurly-subscription')) {
  customElements.define('data-recurly-subscription', class RecurlySubscription extends HTMLElement {
    constructor() {
      super();
      this.observer = null;
      this.priveTermsObserver = null;
    }
    connectedCallback() {
      this.observer = new MutationObserver(() => {
        this.relocateWidget();
      });
      this.observer.observe(document.body, { childList: true, subtree: true });
      this.relocateWidget();
    }
    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.priveTermsObserver) {
        this.priveTermsObserver.disconnect();
        this.priveTermsObserver = null;
      }
      const widget = this.querySelector('#prive-widget-injection-point') || _recurlyWidgetEl;
      if (widget) _recurlyWidgetEl = widget;
    }
    /**
     * Prive sometimes renders subscription summary as "$66every 3 months" (no space before "every").
     * Only text nodes are updated so child elements under .prive-terms stay intact.
     */
    normalizePriveTermsSpacing(termsEl) {
      if (!termsEl) return;
      const walker = document.createTreeWalker(termsEl, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          const parent = node.parentElement;
          if (parent && parent.closest('script, style')) {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_ACCEPT;
        },
      });
      const textNodes = [];
      let node;
      while ((node = walker.nextNode())) {
        textNodes.push(node);
      }
      for (let i = 0; i < textNodes.length; i++) {
        const textNode = textNodes[i];
        const value = textNode.nodeValue;
        if (!value || !value.trim()) continue;
        const next = value.replace(/(\$[\d,.]+)\s*every\b/gi, '$1 every');
        if (next !== value) {
          textNode.nodeValue = next;
        }
      }
    }
    setupPriveTermsSpacing(widgetRoot) {
      const terms = widgetRoot.querySelector('.prive-terms');
      if (!terms) return;

      if (this.priveTermsObserver) {
        this.priveTermsObserver.disconnect();
      }

      this.normalizePriveTermsSpacing(terms);

      this.priveTermsObserver = new MutationObserver(() => {
        this.normalizePriveTermsSpacing(terms);
      });
      this.priveTermsObserver.observe(terms, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
    relocateWidget() {
      const source = _recurlyWidgetEl || document.getElementById('prive-widget-injection-point');
      if (!source) return;

      _recurlyWidgetEl = source;

      if (!this.contains(source)) {
        this.innerHTML = '';
        this.appendChild(source);
      }

      this.setupPriveTermsSpacing(source);
    }
  });
}